import { Menu as BaseMenu } from '@base-ui/react/menu'
import { OTPFieldPreview as BaseOTPField } from '@base-ui/react/otp-field'
import { Select as BaseSelect } from '@base-ui/react/select'
import { createFileRoute } from '@tanstack/react-router'
import {
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Frame } from 'regen-ui'
import AlertTriangle from '~icons/lucide/alert-triangle'
import ArrowLeft from '~icons/lucide/arrow-left'
import Building from '~icons/lucide/building-2'
import Calendar from '~icons/lucide/calendar'
import CheckCircle from '~icons/lucide/check-circle-2'
import ChevronDown from '~icons/lucide/chevron-down'
import ChevronRight from '~icons/lucide/chevron-right'
import HelpCircle from '~icons/lucide/circle-help'
import CreditCard from '~icons/lucide/credit-card'
import ExternalLink from '~icons/lucide/external-link'
import Eye from '~icons/lucide/eye'
import EyeOff from '~icons/lucide/eye-off'
import LoaderCircle from '~icons/lucide/loader-circle'
import RotateCcw from '~icons/lucide/rotate-ccw'
import Settings from '~icons/lucide/settings'
import Upload from '~icons/lucide/upload'
import X from '~icons/lucide/x'

import { ThemeToggle } from '#/ui/ThemeToggle.js'

/**
 * Visual playground for the Stripe headless onramp flow. Renders every
 * `currentStep` of `src/routes/remote/-frames/transaction/Stripe.tsx`
 * without depending on real Stripe credentials, the Link OAuth handshake,
 * or PostHog flag enrolment.
 *
 * Two viewing modes:
 *
 * - **Walkthrough** (default): one card with a clickable footer that
 *   advances through the flow like a real user would.
 * - **Gallery**: every state laid out side-by-side for visual review.
 *
 * Lives at `/design/stripe`.
 */
export const Route = createFileRoute('/design_/stripe')({
  component: StripePlayground,
})

type Step =
  | 'auth'
  | 'checkout'
  | 'complete'
  | 'kyc-1'
  | 'kyc-2'
  | 'loading'
  | 'method'
  | 'payment'
  | 'amount'
  | 'preauth'
  | 'quick-confirm'
  | 'register'
  | 'unavailable'
  | 'waiting'
  | 'wallet'

type Mode = 'walkthrough' | 'gallery'

/**
 * Renders auth + KYC steps as native forms or as the production
 * embedded-iframe placeholders. Defaults to `native` because the
 * native flow is the one we're currently iterating on; flip to
 * `iframe` to preview how the same step would feel when handed off
 * to Stripe-hosted Link / Identity iframes.
 */
type FlowMode = 'native' | 'iframe'

/**
 * Which provider logo anchors the right side of the inset footer
 * band. Defaults to `link` because that's what production Stripe
 * onramps display today — the user signs into their Link account.
 * `stripe` swaps the lockup for the Stripe wordmark so we can
 * preview the alternate framing without forking the row.
 */
type FooterProvider = 'stripe' | 'link'

/** KYC-1 sub-step keys for the multi-step KYC form. */
type KycSubStep = 'identity' | 'ssn' | 'address'

const STEP_LABELS = ['Sign in', 'Verify', 'Pay', 'Done'] as const
type StepIndex = 1 | 2 | 3 | 4

/**
 * Help-icon destinations per provider. Picking the help link based on
 * which provider mark sits next to it keeps the help affordance
 * pointing at the right docs surface — Stripe FAQs (for the Stripe
 * wordmark variant) or Link FAQs (for the Link wordmark variant)
 * instead of a single tempo-side help page that punts to the
 * provider's docs anyway.
 */
const HELP_URL_BY_PROVIDER = {
  link: 'https://link.com/faq',
  stripe: 'https://support.stripe.com/topics/crypto',
} as const

/**
 * Brand themes for the playground host shell. Each theme retones the
 * faux host page behind the modal + maps to one of `regen-ui`'s accent
 * tokens and a brand-appropriate radius. Lite — no Google-Fonts swap;
 * we trust the system font and lean on accent + radius + backdrop
 * for differentiation.
 */
type BrandKey = 'tempo' | 'stripe' | 'axios' | 'uniswap'
type BrandTone = {
  key: BrandKey
  /** Display label for the brand toggle. */
  label: string
  /** `data-regen-color` value applied to the playground root. */
  accent: 'green' | 'blue' | 'red' | 'purple' | 'pink' | 'neutral'
  /** Radius preset (mapped to a CSS var on the playground root). */
  radius: 'small' | 'medium' | 'large'
  /** Tailwind classes for the host-shell page background. */
  hostBackgroundClass: string
  /** Optional URL shown in the host shell URL bar. */
  defaultHost: string
}

const BRAND_TONES: readonly BrandTone[] = [
  {
    key: 'tempo',
    label: 'Tempo',
    accent: 'neutral',
    radius: 'medium',
    hostBackgroundClass:
      'bg-[radial-gradient(circle_at_top_left,rgba(120,140,180,0.10),transparent_60%),radial-gradient(circle_at_bottom_right,rgba(120,180,160,0.08),transparent_55%)]',
    defaultHost: 'app.tempo.xyz/onramp',
  },
  {
    key: 'stripe',
    label: 'Stripe',
    accent: 'purple',
    radius: 'medium',
    hostBackgroundClass:
      'bg-[radial-gradient(circle_at_top_left,rgba(99,91,255,0.18),transparent_60%),radial-gradient(circle_at_bottom_right,rgba(99,91,255,0.10),transparent_55%)]',
    defaultHost: 'dashboard.stripe.com/payments',
  },
  {
    key: 'axios',
    label: 'Axios',
    accent: 'red',
    radius: 'small',
    hostBackgroundClass:
      'bg-[radial-gradient(circle_at_top_left,rgba(245,82,82,0.14),transparent_60%),radial-gradient(circle_at_bottom_right,rgba(40,40,50,0.10),transparent_55%)]',
    defaultHost: 'app.axios.markets/portfolio',
  },
  {
    key: 'uniswap',
    label: 'Uniswap',
    accent: 'pink',
    radius: 'large',
    hostBackgroundClass:
      'bg-[radial-gradient(circle_at_top_left,rgba(255,0,122,0.18),transparent_60%),radial-gradient(circle_at_bottom_right,rgba(255,0,122,0.10),transparent_55%)]',
    defaultHost: 'app.uniswap.org/swap',
  },
] as const

const declineCodeCopy: Record<string, string> = {
  card_declined: 'Your bank declined the charge — try a different card or contact your bank.',
  expired_card: 'That card has expired. Add a different card to continue.',
  generic_decline: 'Your bank declined the charge — try a different card or contact your bank.',
  incorrect_cvc: 'That CVC didn’t match. Re-enter the security code on the back of your card.',
  insufficient_funds: 'Not enough funds. Try a different method.',
  invalid_account: 'That payment method isn’t valid for an onramp purchase. Try another.',
  lost_card: 'That card was reported lost. Add a different card to continue.',
  pickup_card: 'Your bank is holding this card. Try a different card.',
  processing_error: 'Stripe had trouble processing this card. Try again or use another method.',
  stolen_card: 'That card was reported stolen. Add a different card to continue.',
  withdrawal_count_limit_exceeded:
    'Your bank limit for this card has been reached. Try a different method.',
}

function humanizeStripeError(message: string) {
  // The mock scenarios encode a (code: decline_code) in parens at the
  // tail of the error string. Pick those out and prefer our friendly
  // copy when we recognise either code. The raw codes are dropped from
  // the user-facing copy — engineers can find them in the network log.
  const match = /\(([^()]+?)(?:: ([^()]+?))?\)\s*$/.exec(message)
  if (!match) return message
  const code = match[1]?.trim()
  const decline = match[2]?.trim()
  const friendly = (decline && declineCodeCopy[decline]) ?? (code && declineCodeCopy[code]) ?? null
  return friendly ?? message.replace(/\s*\(.*?\)\s*$/, '')
}

/**
 * Generic onramp copy keyed by step. Each pair is a one-line title and
 * a single-line subtitle so the header and async hero can share one
 * source of truth. No trailing ellipses. The preauth subtitle inlines
 * the Link lockup so the user can see which onramp account they're
 * signing into.
 */
function getStepCopy(input: { amountDisplay?: string; email?: string; step: Step }): {
  title: string
  subtitle?: ReactNode
} {
  const { amountDisplay, email, step } = input
  // Form-input steps deliberately ship without a modal subtitle. The
  // amount card + per-step form below carry the user's mental model;
  // a duplicate "do X next" line above is just noise. Brendan's
  // May 19 review: "sub-header is redundant in almost every view".
  if (step === 'preauth') return { title: 'Sign in' }
  if (step === 'register') return { title: 'Create an account' }
  // `auth` is the only form-input step that keeps a subtitle: it
  // confirms the destination address the OTP was sent to, which the
  // 6-cell form below can't echo back on its own.
  if (step === 'auth')
    return {
      title: 'Verify it’s you',
      subtitle: email ? (
        <>
          Enter the 6-digit code sent to <span className="text-foreground">{email}</span>.
        </>
      ) : (
        'Enter the 6-digit code we just sent.'
      ),
    }
  if (step === 'kyc-1') return { title: 'Verify identity' }
  if (step === 'kyc-2') return { title: 'Verify document' }
  if (step === 'amount') return { title: 'Add funds' }
  // `payment` covers both the picker (no saved methods) and the
  // review-saved-methods flow. Title is generic; subtitle anchors
  // the user on the deposit amount they're about to commit to.
  if (step === 'payment')
    return {
      title: 'Choose method',
      subtitle: amountDisplay ? (
        <>
          You are depositing <span className="text-foreground">{amountDisplay}</span>
        </>
      ) : undefined,
    }
  if (step === 'method') return { title: 'Add funds' }
  // Quick-confirm puts the amount in the subtitle (single source of
  // truth at decision time) so the body card can drop the redundant
  // `You are depositing $X` caption under the Deposit button.
  if (step === 'quick-confirm')
    return {
      title: 'Welcome back',
      subtitle: amountDisplay ? (
        <>
          You are depositing <span className="text-foreground">{amountDisplay}</span>
        </>
      ) : undefined,
    }
  // Async hero steps suppress the modal header entirely and surface
  // the copy through the body `CenteredStatus` hero — the subtitle
  // here is the *only* source of truth on these steps. No duplicate
  // "funds arrive shortly" pair like the pre-merge production flow.
  if (step === 'wallet')
    return { title: 'Linking wallet', subtitle: 'Connecting your wallet to your account.' }
  if (step === 'checkout')
    return {
      title: amountDisplay ? `Charging ${amountDisplay} on your card` : 'Charging your card',
      subtitle: 'This usually takes a few seconds.',
    }
  if (step === 'waiting')
    return { title: 'Settling on Tempo', subtitle: 'Finalizing the on-chain transfer.' }
  if (step === 'complete')
    return {
      title: amountDisplay ? `Added ${amountDisplay}` : 'Funds added',
      // Alex's PR suggestion (May 19): the prior copy implied a
      // guarantee we can't keep on slow-network mainnet days.
      subtitle: 'Your funds have arrived.',
    }
  if (step === 'unavailable')
    return { title: 'Onramp unavailable', subtitle: 'Try again in a few minutes.' }
  return { title: 'Loading', subtitle: 'Setting up a secure session.' }
}

function stepIndexFor(step: Step): StepIndex | undefined {
  if (step === 'preauth' || step === 'register' || step === 'auth' || step === 'method') return 1
  if (step === 'kyc-1' || step === 'kyc-2') return 2
  // `quick-confirm` skips KYC entirely — the returning user is
  // already verified — so it sits on the Pay step alongside the
  // regular payment / review screen.
  if (step === 'wallet' || step === 'payment' || step === 'quick-confirm') return 3
  if (step === 'checkout' || step === 'waiting' || step === 'complete') return 4
  return undefined
}

function isKycSubStepValid(
  subStep: KycSubStep,
  kyc: {
    city: string
    dateOfBirth: string
    givenName: string
    idNumber: string
    line1: string
    postalCode: string
    state: string
    surname: string
  },
) {
  if (subStep === 'identity') {
    if (!kyc.givenName.trim() || !kyc.surname.trim()) return false
    if (!kyc.dateOfBirth) return false
    return true
  }
  if (subStep === 'ssn') {
    const digits = kyc.idNumber.replace(/\D/g, '')
    return digits.length === 9
  }
  return (
    !!kyc.line1.trim() &&
    !!kyc.city.trim() &&
    !!kyc.state &&
    /^\d{5}(-\d{4})?$/.test(kyc.postalCode.trim())
  )
}

type ScenarioGroup = {
  id: string
  label: string
  description?: string
  scenarioIds: readonly string[]
}

type StripePaymentToken = {
  card?: { brand?: string | null; funding?: string | null; last4?: string | null } | null
  id: string
  type: string
  us_bank_account?: {
    account_type?: string | null
    bank_name?: string | null
    last4?: string | null
  } | null
}

type Scenario = {
  /** Stable id used by the picker. */
  id: string
  /** Human label shown in the sidebar. */
  label: string
  /** Short description shown above the card body when active. */
  description: string
  /** State the scenario starts in. */
  initialStep: Step
  /** Default email pre-filled in form state. */
  email?: string
  /** Saved payment tokens to seed the picker. */
  savedPaymentTokens?: readonly StripePaymentToken[]
  /** Pre-selected payment token id. */
  paymentTokenId?: string
  /** Forced error message rendered in the alert region. */
  error?: string | undefined
  /** When true, the elementKind starts as "payment" so the embedded element renders. */
  startWithPaymentElement?: boolean
  /** Forced session status (used by waiting/complete). */
  sessionStatus?: 'fulfillment_processing' | 'fulfillment_complete' | 'rejected'
  /** Forced session transaction id. */
  sessionTransactionId?: string | null
  /** When true, "Retry wallet setup" appears (registerWallet failed). */
  walletErrored?: boolean
}

const sampleTokens: readonly StripePaymentToken[] = [
  {
    card: { brand: 'visa', funding: 'credit', last4: '4242' },
    id: 'cpmt_demo_visa_4242',
    type: 'card',
  },
  {
    card: { brand: 'mastercard', funding: 'debit', last4: '5454' },
    id: 'cpmt_demo_mc_5454',
    type: 'card',
  },
  {
    id: 'cpmt_demo_ach',
    type: 'us_bank_account',
    us_bank_account: { account_type: 'checking', bank_name: 'Chase', last4: '6789' },
  },
] as const

const scenarios: readonly Scenario[] = [
  {
    description:
      'Choose how to fund the wallet. Picking a provider routes the user into that flow; this is the entry point before any auth or KYC kicks in.',
    id: 'method',
    initialStep: 'method',
    label: 'Method · choose provider',
  },
  {
    description:
      'Bootstrapping the SDK and resolving the session. Shown briefly while we figure out whether the user already has an onramp account.',
    id: 'loading',
    initialStep: 'loading',
    label: 'Loading',
  },
  {
    description:
      'Onramp provider is offline or misconfigured. Surface a retry plus a route to support so the user is not left at a dead end.',
    error: 'Stripe is not configured.',
    id: 'unavailable',
    initialStep: 'unavailable',
    label: 'Unavailable',
  },
  {
    description:
      'First-time visitor entering their Link email. Submitting the form looks up the account and routes to sign-in, registration, or the verification challenge.',
    email: '',
    id: 'preauth',
    initialStep: 'preauth',
    label: 'Pre-auth · enter email',
  },
  {
    description:
      'No Link account matched this email. Collect full name and phone so we can create one before falling through to the verification step.',
    email: 'jane@example.com',
    id: 'register',
    initialStep: 'register',
    label: 'Register · new Link user',
  },
  {
    description:
      'Account found. Enter the six-digit code we just sent to verify ownership of the email before continuing into KYC.',
    email: 'jane@example.com',
    id: 'auth',
    initialStep: 'auth',
    label: 'Auth · embedded Link',
  },
  {
    description:
      'Collect the identity details our compliance vendor needs — legal name, date of birth, SSN, and mailing address — before we can move funds.',
    email: 'jane@example.com',
    id: 'kyc-1',
    initialStep: 'kyc-1',
    label: 'KYC 1 · identity form',
  },
  {
    description:
      'Hosted document-verification step. The user uploads or photographs an ID; the iframe owns the camera and upload UI end-to-end.',
    email: 'jane@example.com',
    id: 'kyc-2',
    initialStep: 'kyc-2',
    label: 'KYC 2 · documents',
  },
  {
    description:
      'Linking the connected Tempo wallet to the onramp account so future sessions can skip straight to payment. Brief async step.',
    email: 'jane@example.com',
    id: 'wallet-loading',
    initialStep: 'wallet',
    label: 'Wallet · registering',
  },
  {
    description:
      'Wallet registration with the onramp provider failed mid-flight. Show actionable copy and a retry so the user is not stuck on a spinner.',
    email: 'jane@example.com',
    error: 'Wallet registration did not complete.',
    id: 'wallet-error',
    initialStep: 'wallet',
    label: 'Wallet · errored',
    walletErrored: true,
  },
  {
    description:
      'Cash App / Venmo–style amount entry. Reached when the user opts to add more after a successful deposit, or when the host kicks off the flow without a preset amount.',
    email: 'jane@example.com',
    id: 'amount',
    initialStep: 'amount',
    label: 'Amount · enter',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
  },
  {
    description:
      'No saved cards or bank accounts on file. Drop the user into Stripe’s embedded card collector so they can add one without leaving the modal.',
    email: 'jane@example.com',
    id: 'payment-empty',
    initialStep: 'payment',
    label: 'Payment · no saved methods',
    savedPaymentTokens: [],
  },
  {
    description:
      'User reviews the charge against a saved card or bank account. Pick a different method inline or hit Deposit to charge and continue.',
    email: 'jane@example.com',
    id: 'payment-saved',
    initialStep: 'payment',
    label: 'Payment · saved methods',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
  },
  {
    description:
      'Embedded Stripe payment-method element is mounted inside the modal. Used as a transient step in production; surfaced here for visual reference.',
    email: 'jane@example.com',
    id: 'payment-element',
    initialStep: 'payment',
    label: 'Payment · embedded element',
    savedPaymentTokens: sampleTokens,
    startWithPaymentElement: true,
  },
  {
    description:
      'Checkout is in flight with the payment processor. The CTA locks to a loading state until the processor accepts, declines, or errors.',
    email: 'jane@example.com',
    id: 'checkout',
    initialStep: 'checkout',
    label: 'Checkout · in flight',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
  },
  {
    description:
      'Payment captured; Tempo is settling the on-chain transfer. We poll the session until the funds land in the destination wallet.',
    email: 'jane@example.com',
    id: 'waiting',
    initialStep: 'waiting',
    label: 'Waiting · processing',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
    sessionStatus: 'fulfillment_processing',
  },
  {
    description:
      'Funds delivered to the wallet. Surface the amount, optional explorer link, and a single Done CTA to return the user where they came from.',
    email: 'jane@example.com',
    id: 'complete',
    initialStep: 'complete',
    label: 'Complete · success',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
    sessionStatus: 'fulfillment_complete',
    sessionTransactionId: 'cot_1Q9ZAbQwertyDemo',
  },
  {
    description:
      'Returning user with a remembered Link account and a saved card on file. The full email + auth + KYC + select chain collapses to a single confirm screen — Link recognises the device, the saved card is pre-mounted, and the user hits Confirm. Modeled on the Stripe Link one-click pay UX at `link.com/pay`.',
    // Same demo email as every other authed scenario so the value
    // reads as the user's piped-through identity across the whole
    // playground rather than a one-off label.
    email: 'jane@example.com',
    id: 'quick-confirm',
    initialStep: 'quick-confirm',
    label: 'Quick · returning user',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
  },
  {
    description:
      'Card declined at the processor. Render actionable copy mapped from the decline code and let the user swap methods without restarting the flow.',
    email: 'jane@example.com',
    error: 'Your card was declined. (card_declined: insufficient_funds)',
    id: 'payment-declined',
    initialStep: 'payment',
    label: 'Payment · declined',
    paymentTokenId: 'cpmt_demo_visa_4242',
    savedPaymentTokens: sampleTokens,
  },
] as const

const scenarioGroups: readonly ScenarioGroup[] = [
  {
    description: 'Pick how to fund the wallet.',
    id: 'method-picker',
    label: 'Method',
    scenarioIds: ['method'],
  },
  {
    description: 'Bootstrapping the SDK and session state.',
    id: 'bootstrap',
    label: 'Bootstrap',
    scenarioIds: ['loading', 'unavailable'],
  },
  {
    description: 'Email entry, registration, and the embedded auth challenge.',
    id: 'authentication',
    label: 'Authentication',
    scenarioIds: ['preauth', 'register', 'auth'],
  },
  {
    description: 'Identity and document verification.',
    id: 'kyc',
    label: 'Identity (KYC)',
    scenarioIds: ['kyc-1', 'kyc-2'],
  },
  {
    description: 'Linking the wallet to the onramp account.',
    id: 'wallet',
    label: 'Setup',
    scenarioIds: ['wallet-loading', 'wallet-error'],
  },
  {
    description: 'Entering an amount and confirming the payment method.',
    id: 'payment',
    label: 'Method',
    scenarioIds: [
      'amount',
      'payment-empty',
      'payment-saved',
      'payment-element',
      'payment-declined',
    ],
  },
  {
    description:
      'Returning users: Link recognises the device, KYC + card are remembered, the modal collapses to a single confirm step.',
    id: 'returning',
    label: 'Returning',
    scenarioIds: ['quick-confirm'],
  },
  {
    description: 'Checkout, settlement, and success.',
    id: 'checkout',
    label: 'Checkout',
    scenarioIds: ['checkout', 'waiting', 'complete'],
  },
] as const

const scenariosById = new Map(scenarios.map((s) => [s.id, s]))

function StripePlayground() {
  const [scenarioId, setScenarioId] = useState<string>(scenarios[0]!.id)
  // Live step reported up by the active `ScenarioCard` as the user
  // walks through the flow. Used to keep the sidebar highlight in
  // sync with where the user actually is, not just which scenario
  // they originally picked.
  const [liveStep, setLiveStep] = useState<Step>(scenariosById.get(scenarios[0]!.id)!.initialStep)
  const [mode, setMode] = useState<Mode>('walkthrough')
  const [flowMode, setFlowMode] = useState<FlowMode>('native')
  const [provider, setProvider] = useState<FooterProvider>('link')
  // Brand theming is pinned to the neutral Tempo tone now that the
  // brand toggle has been replaced by a footer-provider toggle —
  // the host-shell backdrop still uses the radial gradient, and the
  // playground root still applies `data-regen-radius` so radii feel
  // consistent with shipped UI.
  const brandTone = BRAND_TONES[0]!
  const [hostUrl, setHostUrl] = useState<string>(brandTone.defaultHost)
  const scenario = useMemo(() => scenariosById.get(scenarioId)!, [scenarioId])
  const handleStepChange = useCallback((next: Step) => setLiveStep(next), [])
  const handleScenarioPick = useCallback((id: string) => {
    setScenarioId(id)
    const picked = scenariosById.get(id)
    if (picked) setLiveStep(picked.initialStep)
  }, [])
  // The sidebar highlights whatever scenario best matches the live
  // step. We keep the user's explicit pick as the tiebreaker when
  // multiple scenarios share the same `initialStep` (e.g. the three
  // `payment` flavours) so picking one of them stays sticky.
  const activeScenarioId = (() => {
    const current = scenariosById.get(scenarioId)
    if (current && current.initialStep === liveStep) return scenarioId
    return scenarios.find((s) => s.initialStep === liveStep)?.id ?? scenarioId
  })()
  // The caption follows `activeScenarioId` (not `scenarioId`) so the
  // title/description on the side panel update as the user advances
  // through steps inside a single scenario.
  const activeScenario = scenariosById.get(activeScenarioId) ?? scenario

  return (
    <div
      className="flex min-h-dvh flex-col bg-background text-foreground [&_[data-cta-bold]]:font-semibold"
      data-regen-color={brandTone.accent}
      data-regen-radius={brandTone.radius}
    >
      <header className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-4 border-b border-border bg-surface/95 px-6 py-3 backdrop-blur">
        <div className="flex flex-col">
          <h1 className="text-heading-20">Onramp Playground</h1>
          <p className="text-label-13 text-foreground-secondary">
            A breakdown of all states of the onramp flow.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModeToggle mode={mode} onChange={setMode} />
          {mode === 'walkthrough' && <FlowToggle onChange={setFlowMode} value={flowMode} />}
          <FooterProviderToggle onChange={setProvider} value={provider} />
          <ThemeToggle />
        </div>
      </header>

      {mode === 'walkthrough' ? (
        <main className="flex flex-1 px-4 py-6 sm:px-6 sm:py-8">
          {/*
            Three-column layout on wide screens: scenario picker,
            host shell, then a top-aligned caption beside the host.
            On narrower viewports the caption collapses below the
            host so it never crowds the modal.
          */}
          <div className="grid w-full max-w-[1400px] mx-auto gap-6 grid-cols-1 md:grid-cols-[240px_1fr] xl:gap-10 xl:grid-cols-[240px_minmax(0,720px)_minmax(280px,1fr)]">
            <div className="md:hidden">
              <ScenarioMobileDropdown active={activeScenarioId} onSelect={handleScenarioPick} />
            </div>
            <aside className="hidden md:flex md:items-center md:self-stretch">
              <div className="w-full">
                <ScenarioPicker active={activeScenarioId} onSelect={handleScenarioPick} />
              </div>
            </aside>
            <section className="flex min-h-[calc(100dvh-180px)] flex-col items-center justify-center gap-6 xl:items-stretch xl:justify-start xl:pt-16">
              <HostShell brandTone={brandTone} hostUrl={hostUrl} onHostUrlChange={setHostUrl}>
                <ScenarioCard
                  flowMode={flowMode}
                  key={scenario.id}
                  onStepChange={handleStepChange}
                  provider={provider}
                  scenario={scenario}
                />
              </HostShell>
              <div className="xl:hidden">
                <ScenarioCaption scenario={activeScenario} />
              </div>
            </section>
            <aside className="hidden xl:block xl:pt-16">
              <div className="sticky top-24">
                <ScenarioCaption scenario={activeScenario} />
              </div>
            </aside>
          </div>
        </main>
      ) : (
        <main className="flex flex-1 justify-center px-6 py-10">
          <div className="flex w-full max-w-[1400px] flex-col gap-10">
            {scenarioGroups.map((group) => (
              <section className="flex flex-col gap-3" key={group.id}>
                <header className="flex items-baseline gap-3 border-b border-border pb-2">
                  <h2 className="text-heading-16 text-foreground">{group.label}</h2>
                  {group.description && (
                    <p className="text-label-13 text-foreground-secondary">{group.description}</p>
                  )}
                </header>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(360px,1fr))] gap-6">
                  {group.scenarioIds.map((id) => {
                    const s = scenariosById.get(id)
                    if (!s) return null
                    return (
                      <article className="flex flex-col gap-2" key={id}>
                        <ScenarioCard scenario={s} />
                        <div className="flex flex-col gap-1 px-1 pt-2">
                          <p className="text-label-13 text-foreground">{s.label}</p>
                          <p className="text-label-12 text-foreground-secondary">{s.description}</p>
                        </div>
                      </article>
                    )
                  })}
                </div>
              </section>
            ))}
          </div>
        </main>
      )}
    </div>
  )
}

function ModeToggle(props: { mode: Mode; onChange: (next: Mode) => void }) {
  const { mode, onChange } = props
  return (
    <div className="flex rounded-button border border-border bg-pane p-0.5 text-label-13">
      {(['walkthrough', 'gallery'] as const).map((next) => (
        <button
          aria-pressed={mode === next}
          className={
            mode === next
              ? 'rounded-button bg-surface px-3 py-1 text-foreground shadow-[0_1px_0_rgba(0,0,0,0.04)]'
              : 'rounded-button px-3 py-1 text-foreground-secondary hover:text-foreground'
          }
          key={next}
          onClick={() => onChange(next)}
          type="button"
        >
          {next === 'walkthrough' ? 'Walkthrough' : 'Gallery'}
        </button>
      ))}
    </div>
  )
}

/**
 * Toggle the renderer for auth + KYC steps between the native form
 * (the polished version this playground is iterating on) and the
 * embedded Stripe-hosted iframe placeholder (what production
 * actually mounts today). Visible in walkthrough mode only.
 */
function FlowToggle(props: { onChange: (next: FlowMode) => void; value: FlowMode }) {
  const { onChange, value } = props
  return (
    <div
      aria-label="Flow rendering"
      className="flex rounded-button border border-border bg-pane p-0.5 text-label-13"
      role="tablist"
    >
      {(['native', 'iframe'] as const).map((next) => (
        <button
          aria-pressed={value === next}
          className={
            value === next
              ? 'rounded-button bg-surface px-3 py-1 text-foreground shadow-[0_1px_0_rgba(0,0,0,0.04)]'
              : 'rounded-button px-3 py-1 text-foreground-secondary hover:text-foreground'
          }
          key={next}
          onClick={() => onChange(next)}
          type="button"
        >
          {next === 'native' ? 'Native' : 'iFrame'}
        </button>
      ))}
    </div>
  )
}

/**
 * Footer-provider picker. Replaces the brand-theme toggle in the
 * playground header — instead of retoning the host shell we toggle
 * the lockup at the right side of the inset footer band between
 * the Link mark (production default) and the Stripe wordmark.
 */
function FooterProviderToggle(props: {
  onChange: (next: FooterProvider) => void
  value: FooterProvider
}) {
  const { onChange, value } = props
  return (
    <div
      aria-label="Footer provider"
      className="flex rounded-button border border-border bg-pane p-0.5 text-label-13"
      role="tablist"
    >
      {(['link', 'stripe'] as const).map((next) => (
        <button
          aria-pressed={value === next}
          className={
            value === next
              ? 'rounded-button bg-surface px-3 py-1 text-foreground shadow-[0_1px_0_rgba(0,0,0,0.04)]'
              : 'rounded-button px-3 py-1 text-foreground-secondary hover:text-foreground'
          }
          key={next}
          onClick={() => onChange(next)}
          type="button"
        >
          {next === 'link' ? 'Link' : 'Stripe'}
        </button>
      ))}
    </div>
  )
}

/**
 * Scenarios that don't belong in the walkthrough sidebar. The
 * iframe payment-element placeholder isn't a meaningful destination
 * the user can land on directly — production uses it only as a
 * transient step inside the natural flow — so we hide it from the
 * walkthrough picker and let it surface only in the gallery.
 */
const WALKTHROUGH_EXCLUDED_SCENARIO_IDS = new Set(['payment-element'])

function walkthroughGroups(): readonly ScenarioGroup[] {
  return scenarioGroups
    .map((group) => ({
      ...group,
      scenarioIds: group.scenarioIds.filter((id) => !WALKTHROUGH_EXCLUDED_SCENARIO_IDS.has(id)),
    }))
    .filter((group) => group.scenarioIds.length > 0)
}

function ScenarioPicker(props: { active: string; onSelect: (id: string) => void }) {
  const { active, onSelect } = props
  const groups = walkthroughGroups()
  const total = groups.reduce((n, g) => n + g.scenarioIds.length, 0)
  return (
    <aside className="flex max-h-[calc(100dvh-180px)] flex-col gap-2 self-start">
      <div className="flex items-baseline justify-between px-1">
        <p className="text-label-12 font-medium tracking-wide text-foreground-secondary uppercase">
          Scenarios
        </p>
        <span className="text-label-12 text-foreground-secondary/60">{total}</span>
      </div>
      <p className="px-1 pb-1 text-label-12 text-foreground-secondary/70">
        The footer buttons advance through the real flow. Switch scenarios to reset.
      </p>
      <nav
        aria-label="Scenarios"
        className="flex flex-col gap-3 overflow-y-auto rounded-body border border-border bg-pane p-2"
      >
        {groups.map((group) => (
          <div className="flex flex-col gap-1" key={group.id}>
            <p className="px-2 pt-1 pb-0.5 text-label-12 font-medium text-foreground-secondary/80">
              {group.label}
            </p>
            <ul className="ml-2 flex flex-col gap-0.5 border-l border-border/60 pl-2">
              {group.scenarioIds.map((id) => {
                const s = scenariosById.get(id)
                if (!s) return null
                const isActive = active === id
                return (
                  <li key={id}>
                    <button
                      aria-current={isActive ? 'true' : undefined}
                      className={
                        isActive
                          ? 'w-full cursor-default rounded-button bg-surface px-2.5 py-1.5 text-left text-label-13 text-foreground'
                          : 'w-full cursor-pointer rounded-button px-2.5 py-1.5 text-left text-label-13 text-foreground-secondary hover:bg-secondary-hover hover:text-foreground'
                      }
                      onClick={() => onSelect(id)}
                      type="button"
                    >
                      {scenarioLeafLabel(s.label)}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  )
}

/**
 * Strip the group prefix (`Method · `) and capitalize only the very
 * first letter so labels like `embedded Link` keep their proper-noun
 * casing intact.
 */
function scenarioLeafLabel(label: string): string {
  const leaf = label.replace(/^[^·]+·\s*/, '') || label
  if (!leaf) return leaf
  return leaf.charAt(0).toUpperCase() + leaf.slice(1)
}

/**
 * Compact mobile-only scenario picker. Renders as a `<details>` so it
 * collapses by default — keeps the modal as the focal element on
 * narrow viewports instead of being shoved below a tall full-width
 * sidebar.
 */
function ScenarioMobileDropdown(props: { active: string; onSelect: (id: string) => void }) {
  const { active, onSelect } = props
  const groups = walkthroughGroups()
  const activeScenario = scenariosById.get(active)
  const activeGroup = groups.find((g) => g.scenarioIds.includes(active as never))
  return (
    <details className="group rounded-body border border-border bg-pane">
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2.5 list-none [&::-webkit-details-marker]:hidden">
        <span className="flex flex-col">
          <span className="text-label-12 font-medium text-foreground-secondary">
            {activeGroup?.label ?? 'Scenario'}
          </span>
          <span className="text-label-13 text-foreground">
            {activeScenario ? scenarioLeafLabel(activeScenario.label) : '—'}
          </span>
        </span>
        <ChevronDown
          aria-hidden
          className="size-4 text-foreground-secondary transition-transform duration-200 group-open:rotate-180"
        />
      </summary>
      <nav
        aria-label="Scenarios"
        className="flex max-h-[60dvh] flex-col gap-3 overflow-y-auto border-t border-border p-2"
      >
        {groups.map((group) => (
          <div className="flex flex-col gap-1" key={group.id}>
            <p className="px-2 pt-1 pb-0.5 text-label-12 font-medium text-foreground-secondary/80">
              {group.label}
            </p>
            <ul className="ml-2 flex flex-col gap-0.5 border-l border-border/60 pl-2">
              {group.scenarioIds.map((id) => {
                const s = scenariosById.get(id)
                if (!s) return null
                const isActive = active === id
                return (
                  <li key={id}>
                    <button
                      aria-current={isActive ? 'true' : undefined}
                      className={
                        isActive
                          ? 'w-full cursor-default rounded-button bg-surface px-2.5 py-1.5 text-left text-label-13 text-foreground'
                          : 'w-full cursor-pointer rounded-button px-2.5 py-1.5 text-left text-label-13 text-foreground-secondary hover:bg-secondary-hover hover:text-foreground'
                      }
                      onClick={() => onSelect(id)}
                      type="button"
                    >
                      {scenarioLeafLabel(s.label)}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>
    </details>
  )
}

/**
 * Browser-chrome host surface. Grounds the modal so it reads as
 * floating on top of "the host app" instead of in a void. The URL bar
 * is decorative — we don't render an actual `<iframe>` because most
 * production sites refuse to be framed (X-Frame-Options / CSP).
 */
function HostShell(props: {
  brandTone: BrandTone
  children: ReactNode
  hostUrl: string
  onHostUrlChange: (next: string) => void
}) {
  const { brandTone, children, hostUrl, onHostUrlChange } = props
  return (
    <div className="relative w-full max-w-[720px] overflow-hidden rounded-[14px] border border-border bg-pane shadow-[0_24px_48px_-24px_rgba(0,0,0,0.18)]">
      <div className="flex items-center gap-2 border-b border-border bg-surface px-3 py-2">
        <span aria-hidden className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-[#FF5F56]" title="Close" />
          <span className="size-2.5 rounded-full bg-[#FFBD2E]" title="Minimize" />
          <span className="size-2.5 rounded-full bg-[#27C93F]" title="Expand" />
        </span>
        <input
          aria-label="Host URL"
          className="ml-2 min-w-0 flex-1 rounded-button border border-border bg-pane px-2 py-1 text-center text-label-12 text-foreground-secondary/80 outline-none placeholder:text-foreground-secondary/40 focus:text-foreground focus-visible:border-focus"
          onChange={(event) => onHostUrlChange(event.target.value)}
          placeholder="app.tempo.xyz/onramp"
          spellCheck={false}
          value={hostUrl}
        />
        <span aria-hidden className="inline-flex items-center gap-1 text-foreground-secondary/60">
          <OnePasswordGlyph className="size-4" />
          <Settings className="size-3.5" />
        </span>
      </div>
      <div
        className={
          'relative flex min-h-[480px] items-center justify-center px-4 py-10 ' +
          brandTone.hostBackgroundClass
        }
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] [background-size:24px_24px]"
        />
        <div className="relative">{children}</div>
      </div>
    </div>
  )
}

/** Tiny stylized 1Password lock-in-keyhole glyph (decorative only). */
function OnePasswordGlyph(props: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={props.className}
      fill="currentColor"
      role="img"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8 1.5a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8 1.5zm0 1.4a5.1 5.1 0 1 1 0 10.2A5.1 5.1 0 0 1 8 2.9zm0 2.5a2.6 2.6 0 0 0-1.05 4.97v1.83l1.05-1 1.05 1V10.4A2.6 2.6 0 0 0 8 5.4zm0 1.4a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4z" />
    </svg>
  )
}

/**
 * Caption block under the modal. Two-line lockup: a small mono
 * uppercase tag for the scenario group, then the scenario leaf as a
 * proper title. Pinned to modal width and left-aligned.
 */
function ScenarioCaption(props: { scenario: Scenario }) {
  const { scenario } = props
  const [groupRaw, leafRaw] = splitScenarioLabel(scenario.label)
  const tag = groupRaw ?? 'Scenario'
  const title = leafRaw ? leafRaw.charAt(0).toUpperCase() + leafRaw.slice(1) : scenario.label
  return (
    <div className="flex w-full max-w-[440px] flex-col items-start gap-2 text-left">
      <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground-secondary/80">
        {tag}
      </p>
      <h2 className="text-heading-20 text-foreground">{title}</h2>
      <p className="text-copy-15 text-foreground-secondary">
        <InlineMarkdown text={scenario.description} />
      </p>
    </div>
  )
}

/**
 * Minimal inline markdown renderer — supports backtick-wrapped
 * spans rendered as `<code>`. Splits on the backtick token so we
 * don't pull in a full markdown library for a single inline format.
 */
function InlineMarkdown(props: { text: string }) {
  const { text } = props
  const parts = text.split(/(`[^`]+`)/g)
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
          return (
            <code
              className="rounded-[4px] bg-secondary px-1 py-0.5 font-mono text-[0.92em] text-foreground"
              key={i}
            >
              {part.slice(1, -1)}
            </code>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

function splitScenarioLabel(label: string): [string | undefined, string | undefined] {
  const idx = label.indexOf('·')
  if (idx === -1) return [undefined, label]
  return [label.slice(0, idx).trim(), label.slice(idx + 1).trim()]
}

function ScenarioCard(props: {
  flowMode?: FlowMode
  onStepChange?: ((step: Step) => void) | undefined
  provider?: FooterProvider
  scenario: Scenario
}) {
  const { flowMode = 'native', onStepChange, provider = 'link', scenario } = props
  const [step, setStep] = useState<Step>(scenario.initialStep)
  // Mirror the live step up to the parent so the sidebar highlight
  // follows the user as they walk through the flow.
  useEffect(() => {
    onStepChange?.(step)
  }, [onStepChange, step])
  const [email, setEmail] = useState(scenario.email ?? '')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [paymentToken, setPaymentToken] = useState<string | undefined>(scenario.paymentTokenId)
  const [paymentElementOpen, setPaymentElementOpen] = useState(
    scenario.startWithPaymentElement ?? false,
  )
  const [error, setError] = useState<string | undefined>(scenario.error)
  const [walletRetrying, setWalletRetrying] = useState(false)
  const walletErrored = scenario.walletErrored ?? false
  const [kycSubStep, setKycSubStep] = useState<KycSubStep>('identity')
  // Tracks whether the user has clicked Continue on a sub-step that
  // failed validation. Used to flip required fields into the error
  // palette and surface a one-line message above the footer.
  const [kycAttempted, setKycAttempted] = useState(false)
  // Set when the user opts out of providing the required US ID or
  // address. We treat this as a terminal "needs another rail" screen
  // — the body switches to a centered status hero and the footer
  // CTAs swap to [back] + [deposit with crypto].
  const [kycBlocked, setKycBlocked] = useState<'no-address' | 'no-id' | undefined>(undefined)

  const [kyc, setKyc] = useState(() => ({
    city: '',
    dateOfBirth: '',
    givenName: '',
    idNumber: '',
    line1: '',
    line2: '',
    postalCode: '',
    state: '',
    surname: '',
  }))

  // Default to sample tokens so the walkthrough lands on the native
  // PaymentSelect when the user advances naturally from `wallet` to
  // `payment` (scenarios upstream of payment don't carry tokens).
  // Scenarios that *want* to demo the empty state set
  // `savedPaymentTokens: []` explicitly.
  const savedPaymentTokens = scenario.savedPaymentTokens ?? sampleTokens

  // Free-form amount (raw user input, e.g. "50" or "12.34"). The
  // formatted `$X.XX` rendering lives in `amountDisplay`, derived
  // from this state. Defaults to `50` so the canonical screens
  // demo a realistic deposit; the amount-entry scenario resets to
  // an empty string so the user starts with the placeholder.
  const [customAmount, setCustomAmount] = useState(scenario.initialStep === 'amount' ? '' : '50')
  const amountDisplay = useMemo(() => formatAmountDisplay(customAmount), [customAmount])
  // Sample existing wallet balance ($120.00) plus the amount the
  // user is depositing — surfaced on the Complete step so the user
  // closes the modal with a concrete "this much is in my wallet
  // now" anchor, per Brendan's May 19 ask.
  const walletBalanceDisplay = useMemo(() => {
    const parsed = Number.parseFloat(customAmount)
    const baseline = 120
    const total = Number.isFinite(parsed) ? baseline + parsed : baseline
    return new Intl.NumberFormat('en-US', { currency: 'USD', style: 'currency' }).format(total)
  }, [customAmount])
  // "Remember this payment method" toggle on the payment-saved step.
  // Persists the user's preference to skip the email / OTP / KYC /
  // method-pick chain on subsequent deposits from this device. In the
  // playground it's purely visual; production would persist the
  // preference via Stripe Link's signed-in account cookie so any
  // Tempo-powered host page recognises the returning user.
  const [rememberMethod, setRememberMethod] = useState(true)

  const goPreauth = useCallback(() => {
    setStep('preauth')
    setPaymentElementOpen(false)
    setError(undefined)
  }, [])

  const handleStartAuth = useCallback(() => {
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.')
      return
    }
    setError(undefined)
    setStep('auth')
  }, [email])

  const handleRegister = useCallback(() => {
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.')
      return
    }
    if (!normalizePhone(phone)) {
      setError('Enter a valid phone number.')
      return
    }
    setError(undefined)
    setStep('auth')
  }, [email, phone])

  const handleSubmitKyc = useCallback(() => {
    setStep('wallet')
  }, [])

  // SSN is folded into the identity sub-step; address always follows.
  const handleSubmitKycSubStep = useCallback(() => {
    setKycSubStep('address')
  }, [])

  const retreatKycSubStep = useCallback(() => {
    if (kycSubStep === 'address') setKycSubStep('identity')
    else goPreauth()
  }, [goPreauth, kycSubStep])

  // Two-stage `kyc-2` flow for the iframe handoff path. `notice` is
  // the initial screen: a friendly banner explaining that the user
  // will be handed off to Stripe Identity, with `Verify identity
  // ↗` as the CTA. `iframe` is the mocked Stripe Identity surface:
  // a dashed-border iframe placeholder + a `Continue` CTA that
  // actually advances the flow. Production renders the Stripe
  // Identity iframe in the same slot.
  const [kyc2Stage, setKyc2Stage] = useState<'notice' | 'iframe'>('notice')
  const handleVerifyDocuments = useCallback(() => {
    setKyc2Stage('iframe')
  }, [])
  const handleKyc2Continue = useCallback(() => {
    setKyc2Stage('notice')
    setStep('wallet')
  }, [])

  const handleRetryWallet = useCallback(() => {
    setWalletRetrying(true)
    setError(undefined)
    setTimeout(() => {
      setWalletRetrying(false)
      setStep('payment')
    }, 600)
  }, [])

  const handleCollectPayment = useCallback(() => {
    setPaymentElementOpen(true)
  }, [])

  const handleConfirmPaymentElement = useCallback(() => {
    setPaymentElementOpen(false)
    setPaymentToken(savedPaymentTokens[0]?.id ?? 'cpmt_demo_visa_4242')
  }, [savedPaymentTokens])

  const handleStartCheckout = useCallback(() => {
    // Demo validation hook: the Visa 4242 saved card always fails
    // with an insufficient-funds decline so the playground can
    // exercise the error path. Any other token succeeds.
    if (paymentToken === 'cpmt_demo_visa_4242') {
      setError('Your card was declined. (card_declined: insufficient_funds)')
      return
    }
    setError(undefined)
    // Per Brendan's May 19 review: `processing` was too fast to read.
    // Hold each beat for ~1.6s minimum so the user sees the copy
    // change. Total path = checkout (1.6s) → waiting (1.8s) → complete.
    setStep('checkout')
    setTimeout(() => setStep('waiting'), 1600)
    setTimeout(() => setStep('complete'), 3400)
  }, [paymentToken])

  const onDismiss = () => {
    setStep('method')
    setError(undefined)
    setPaymentElementOpen(false)
  }

  const elementKind = paymentElementOpen ? 'payment' : undefined
  const isMethodStep = step === 'method'
  const isInFlight = step === 'checkout' || step === 'waiting'
  // Steps where a CenteredStatus hero in the body carries the title
  // and subtitle; the modal header is suppressed for these.
  const isPaymentLoadingHero =
    step === 'payment' && elementKind !== 'payment' && savedPaymentTokens.length === 0
  const isHeroStep =
    step === 'loading' ||
    step === 'unavailable' ||
    step === 'complete' ||
    isInFlight ||
    step === 'wallet' ||
    isPaymentLoadingHero ||
    (step === 'kyc-1' && !!kycBlocked)
  const stepIndicator = !isMethodStep ? stepIndexFor(step) : undefined
  const stepCopy = getStepCopy({ amountDisplay, email, step })

  const showError =
    !!error &&
    !isInFlight &&
    step !== 'complete' &&
    step !== 'unavailable' &&
    step !== 'payment' &&
    !(step === 'wallet' && walletErrored)
  const errorBody = error ? humanizeStripeError(error) : null

  // Identity strip is shown once the user has supplied an email —
  // i.e. anywhere after they leave the email-entry preauth step. The
  // 'auth' and 'register' steps are post-email-entry so they show
  // the footer with the email already filled in.
  // Steps where the user is past the email-entry preauth step and
  // we have an account anchor to render the inset footer band
  // against. `register` is intentionally excluded — the user has
  // typed an email but hasn't created an account yet, so the
  // `Powered by Link` lockup + identity strip is premature.
  const authedSteps =
    step === 'auth' ||
    step === 'kyc-1' ||
    step === 'kyc-2' ||
    step === 'wallet' ||
    step === 'amount' ||
    step === 'payment' ||
    step === 'checkout' ||
    step === 'waiting' ||
    step === 'complete'
  const footerRowEmail = email && authedSteps ? email : undefined
  // Hide the footer entirely until the user has supplied an email
  // and reached an authenticated step. Pre-auth there is no account
  // to anchor the band against, so the help + provider logo
  // wouldn't have anything meaningful to sit next to.
  const showFooterRow = authedSteps && !!email && step !== 'complete'

  // Reset attempted state whenever the sub-step transitions so the
  // user gets a clean slate on each form.
  useEffect(() => {
    setKycAttempted(false)
  }, [kycSubStep, step])

  // The wallet step has no user action — it represents the async
  // wallet-linking call. Auto-advance to `payment` after a short
  // beat so the walkthrough doesn't visibly stall on this hero. The
  // errored variant stays put because that one has a Retry CTA.
  useEffect(() => {
    if (step !== 'wallet' || walletErrored) return
    const id = setTimeout(() => setStep('payment'), 1500)
    return () => clearTimeout(id)
  }, [step, walletErrored])

  return (
    <div className="relative w-[360px] overflow-hidden rounded-body border border-border bg-surface [--regen-accent:var(--regen-accent-blue)]">
      {stepIndicator && (
        // Tight padding — the dots sit ~14px from modal top, then
        // the ModalHeader follows with `pt-2`. Was `pt-3 pb-2` which
        // pushed the title ~40px down (Brendan, May 19).
        <div className="px-5 pt-2 pb-1">
          <StepDots current={stepIndicator} />
        </div>
      )}
      <Frame
        // Homogenise inter-element spacing across the body. Default
        // Frame body is `gap-4 pb-4` (16px) which makes the visible
        // gap between last input and first button feel ~50% larger
        // than the inter-input gap; here we drop both to 12px so the
        // form reads as a single column of evenly-spaced rows that
        // continues into the button stack below.
        bodyClassName={step === 'kyc-1' ? 'gap-2 pb-3' : 'gap-3 pb-3'}
        footer={
          <div className="flex flex-col gap-3">
            {renderFooter({
              amountDisplay,
              walletBalanceDisplay,
              elementKind,
              email,
              flowMode,
              handleAuthResend: () => alert('resend not wired'),
              handleAuthVerify: () => setStep('kyc-1'),
              handleConfirmPaymentElement,
              handleCollectPayment,
              handleKycBack: retreatKycSubStep,
              handleRegister,
              handleRetryWallet,
              handleStartAuth,
              handleStartCheckout,
              handleSubmitKyc: () => {
                if (!isKycSubStepValid('address', kyc)) {
                  setKycAttempted(true)
                  return
                }
                setKycAttempted(false)
                handleSubmitKyc()
              },
              handleSubmitKycSubStep: () => {
                if (kycSubStep === 'identity' && !isKycSubStepValid('identity', kyc)) {
                  setKycAttempted(true)
                  return
                }
                if (kycSubStep === 'address' && !isKycSubStepValid('address', kyc)) {
                  setKycAttempted(true)
                  return
                }
                setKycAttempted(false)
                handleSubmitKycSubStep()
              },
              handleDepositCrypto: () => alert('crypto onramp not wired'),
              handleClearBlocked: () => setKycBlocked(undefined),
              kycBlocked,
              handleVerifyDocuments,
              handleKyc2Continue,
              kyc2Stage,
              handleAddMore: () => {
                setCustomAmount('')
                setStep('amount')
                setError(undefined)
              },
              handleAmountContinue: () => setStep('payment'),
              canContinueAmount: parseFloat(customAmount || '0') > 0,
              handleDone: onDismiss,
              kyc,
              kycSubStep,
              onBack: goPreauth,
              onChooseMethod: () => setStep('preauth'),
              savedPaymentTokens,
              step,
              walletErrored,
              walletRetrying,
            })}
            {showFooterRow && (
              <OnrampFooterRow
                disableHelp={isInFlight}
                email={footerRowEmail}
                provider={provider}
              />
            )}
          </div>
        }
        variant="plain"
      >
        {!isHeroStep && (
          <ModalHeader
            compactTop={!!stepIndicator}
            onDismiss={onDismiss}
            subtitle={stepCopy.subtitle}
            title={stepCopy.title}
          />
        )}
        {isHeroStep && <FloatingDismiss onClick={onDismiss} />}

        {showError && (
          <Banner kind="error" icon={<AlertTriangle aria-hidden className="size-4" />}>
            {errorBody}
          </Banner>
        )}

        {step === 'loading' && (
          <CenteredStatus kind="loading" title={stepCopy.title} subtitle={stepCopy.subtitle} />
        )}

        {/*
          `wallet` linking is a sub-second async beat — not its own
          page. Render the destination body (the payment picker the
          user is about to land on) underneath a translucent
          backdrop-blur overlay with a compact spinner + label, so
          the transition reads as a single continuous step instead
          of an interstitial. The errored variant keeps the full
          hero treatment because that's a destination state with
          a Retry CTA.
        */}
        {step === 'wallet' && !walletErrored && (
          <div className="relative">
            <div aria-hidden className="pointer-events-none opacity-40 blur-[1px]">
              <PaymentPickerPreview />
            </div>
            <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/90 px-3 py-1.5 shadow-md">
                <LoaderCircle
                  aria-hidden
                  className="size-4 animate-rgn-spin text-foreground-secondary"
                />
                <span className="text-label-13 text-foreground">Linking account</span>
              </div>
            </div>
          </div>
        )}

        {step === 'wallet' && walletErrored && (
          <CenteredStatus
            kind="error"
            title="Setup didn't complete"
            subtitle="We couldn't link your wallet to your onramp account."
          />
        )}

        {isMethodStep && <MethodPicker onPick={() => setStep('preauth')} />}

        {step === 'unavailable' && (
          <CenteredStatus
            kind="error"
            title={stepCopy.title}
            subtitle={error ?? stepCopy.subtitle}
          />
        )}

        {step === 'preauth' && (
          <FormSection>
            <NestedField
              autoCapitalize="none"
              autoComplete="email webauthn"
              autoFocus
              data-form-type="email"
              enterKeyHint="next"
              inputClassName="text-copy-15"
              inputMode="email"
              label="Email"
              name="link-email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              type="email"
              value={email}
            />
          </FormSection>
        )}

        {step === 'register' && (
          <>
            {/*
              No `No account on this email yet` banner and no `Tell
              us about you` section heading — both are redundant
              with the Frame title `Create an account`. The two
              fields read as the form by themselves; the affordance
              to use a different email lives in the footer below
              the primary CTAs (see `renderFooterButtons`).
            */}
            <NestedField
              autoComplete="name"
              autoFocus
              data-1p-ignore="true"
              data-form-type="other"
              data-lpignore="true"
              label="Full name"
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Jane Doe"
              type="text"
              value={fullName}
            />
            <NestedField
              autoComplete="tel"
              data-1p-ignore="true"
              data-form-type="other"
              data-lpignore="true"
              inputMode="tel"
              label="Phone number"
              onChange={(event) => setPhone(event.target.value)}
              placeholder="+1 415 555 1234"
              type="tel"
              value={phone}
            />
          </>
        )}

        {/* Auth step: either a native one-time-code challenge (the
            polished native flow) or the hosted-iframe placeholder
            that mirrors production's Link auth iframe. The FlowToggle
            in the playground header swaps between them. */}
        {step === 'auth' && flowMode === 'native' && (
          <NativeAuthChallenge onVerify={() => setStep('kyc-1')} />
        )}
        {step === 'auth' && flowMode === 'iframe' && (
          <FakeEmbeddedElement
            kind="auth"
            onComplete={() => setStep('kyc-1')}
            primaryLabel="Verify"
          />
        )}

        {step === 'kyc-1' &&
          (kycBlocked ? (
            <CenteredStatus
              kind="error"
              subtitle={
                kycBlocked === 'no-id'
                  ? "We can't verify your identity without one. Fund this wallet with crypto from another network instead."
                  : 'Fund this wallet with crypto from another network instead.'
              }
              title={
                kycBlocked === 'no-id'
                  ? 'This onramp needs a US ID.'
                  : 'This onramp needs a US mailing address.'
              }
            />
          ) : flowMode === 'iframe' ? (
            <FakeEmbeddedElement
              kind="auth"
              onComplete={() => setStep('wallet')}
              primaryLabel="Verify identity"
            />
          ) : (
            <KycSubFlow
              attempted={kycAttempted}
              kyc={kyc}
              onBlock={setKycBlocked}
              onChange={(next) => setKyc((current) => ({ ...current, ...next }))}
              subStep={kycSubStep}
            />
          ))}

        {/*
          KYC-2 iframe handoff. Two stages so reviewers can preview
          both halves of the production flow: a notice screen
          explaining the handoff (`notice`, default), then the
          mocked Stripe Identity iframe surface (`iframe`, after
          clicking Verify identity). Native flowMode still shows
          the inline document-upload tiles for a non-handoff
          alternative.
        */}
        {step === 'kyc-2' && flowMode === 'iframe' && kyc2Stage === 'notice' && (
          <Kyc2HandoffNotice />
        )}
        {step === 'kyc-2' && flowMode === 'iframe' && kyc2Stage === 'iframe' && (
          <Kyc2IdentityIframe />
        )}
        {step === 'kyc-2' && flowMode === 'native' && <NativeDocumentUpload />}

        {/* Payment-element step: hosted-iframe placeholder for card
            collection. Production renders the Stripe payment element
            iframe; the playground mocks it the same way as auth. */}
        {step === 'payment' && elementKind === 'payment' && (
          <FakeEmbeddedElement
            kind="payment"
            onComplete={handleConfirmPaymentElement}
            primaryLabel="Save method"
          />
        )}

        {step === 'amount' && (
          <AmountEntry
            amount={customAmount}
            method={savedPaymentTokens.find((token) => token.id === paymentToken)}
            onAmountChange={setCustomAmount}
            onMethodClick={() => setStep('payment')}
          />
        )}

        {step === 'payment' && elementKind !== 'payment' && savedPaymentTokens.length > 0 && (
          <div className="flex flex-col gap-2">
            {/*
              Method picker — `METHOD` label lives INSIDE the field
              now (left-side, vertically centered) and the
              `+ Add another` affordance is the last item in the
              dropdown popup, not a sibling button above. Single
              control, single source of truth.
            */}
            <PaymentSelect
              invalid={!!error}
              onAddNew={handleCollectPayment}
              onChange={(value) => {
                setPaymentToken(value)
                setError(undefined)
              }}
              tokens={savedPaymentTokens}
              value={paymentToken}
            />
            {error && <FieldErrorMessage>{humanizeStripeError(error)}</FieldErrorMessage>}
            {/*
              "Remember this card" toggle. When the user opts in, the
              next time they hit this onramp from any host app they
              skip the email / OTP / KYC / method-pick chain entirely
              and land on the `quick-confirm` step (see the
              `Returning · quick-confirm` scenario for the visual).
              Stored client-side only in the playground — production
              would persist via the Stripe Link account cookie.
            */}
            <RememberMethodToggle checked={rememberMethod} onChange={setRememberMethod} />
          </div>
        )}

        {step === 'quick-confirm' && (
          <QuickConfirmLockup
            email={email}
            error={error}
            onAddNewMethod={handleCollectPayment}
            onChangeAccount={() => setStep('preauth')}
            onPaymentTokenChange={(next) => {
              setPaymentToken(next)
              setError(undefined)
            }}
            paymentToken={paymentToken}
            tokens={savedPaymentTokens}
          />
        )}

        {step === 'payment' && elementKind !== 'payment' && savedPaymentTokens.length === 0 && (
          <CenteredStatus
            kind="loading"
            title="Loading payment form"
            subtitle="Preparing a secure card field."
          />
        )}

        {isInFlight && (
          <CenteredStatus kind="loading" title={stepCopy.title} subtitle={stepCopy.subtitle} />
        )}

        {step === 'complete' && (
          <CenteredStatus kind="success" title={stepCopy.title} subtitle={stepCopy.subtitle} />
        )}
      </Frame>
    </div>
  )
}

type FooterInput = {
  amountDisplay: string
  walletBalanceDisplay: string
  elementKind: 'payment' | undefined
  email: string
  flowMode: FlowMode
  handleAuthResend: () => void
  handleAuthVerify: () => void
  handleConfirmPaymentElement: () => void
  handleCollectPayment: () => void
  handleKycBack: () => void
  handleRegister: () => void
  handleRetryWallet: () => void
  handleStartAuth: () => void
  handleStartCheckout: () => void
  handleSubmitKyc: () => void
  handleSubmitKycSubStep: () => void
  handleVerifyDocuments: () => void
  handleKyc2Continue: () => void
  kyc2Stage: 'notice' | 'iframe'
  handleAddMore: () => void
  handleAmountContinue: () => void
  canContinueAmount: boolean
  handleDone: () => void
  handleDepositCrypto: () => void
  handleClearBlocked: () => void
  kyc: KycValue
  kycSubStep: KycSubStep
  kycBlocked: 'no-address' | 'no-id' | undefined
  onBack: () => void
  onChooseMethod: () => void
  savedPaymentTokens: readonly StripePaymentToken[]
  step: Step
  walletErrored: boolean
  walletRetrying: boolean
}

function renderFooter(input: FooterInput) {
  const buttons = renderFooterButtons(input)
  if (!buttons) return null
  return <div className="flex flex-col gap-2">{buttons}</div>
}

function renderFooterButtons(input: FooterInput) {
  const {
    elementKind,
    handleRegister,
    handleRetryWallet,
    handleStartAuth,
    handleStartCheckout,
    handleSubmitKyc,
    handleSubmitKycSubStep,
    handleVerifyDocuments,
    handleKyc2Continue,
    kyc2Stage,
    kycSubStep,
    onBack,
    onChooseMethod,
    savedPaymentTokens,
    step,
    walletErrored,
    walletRetrying,
  } = input

  if (step === 'method') return null

  if (step === 'preauth') {
    const valid = isValidEmail(input.email ?? '')
    return (
      <Button
        className="w-full"
        disabled={!valid}
        onClick={handleStartAuth}
        size="medium"
        variant="primary"
      >
        {valid ? 'Continue' : 'Email required'}
      </Button>
    )
  }

  if (step === 'register')
    return (
      // Two-row footer:
      //   Row 1: full-width primary `Create account`.
      //   Row 2: square Back + outline `Use a different email`.
      // The primary commit lives on its own line so it dominates;
      // the dismiss-style affordances pair on the second row. The
      // outline button uses a bordered transparent style so it
      // reads as a *de-emphasised* sibling of the back button — both
      // are escape hatches, not commits.
      <div className="flex flex-col gap-2">
        <Button className="w-full" onClick={handleRegister} size="medium" variant="primary">
          Create account
        </Button>
        <FooterRow>
          <BackButton onClick={onBack} />
          <button
            className="flex h-10 flex-1 items-center justify-center rounded-button border border-border bg-transparent px-4 text-copy-15 text-foreground transition-colors hover:bg-secondary-hover focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
            onClick={onBack}
            type="button"
          >
            Use a different email
          </button>
        </FooterRow>
      </div>
    )

  if (step === 'auth')
    return (
      <FooterRow>
        <BackButton onClick={onBack} />
        {input.flowMode === 'native' && (
          <ResendButton className="flex-1" onResend={input.handleAuthResend} />
        )}
        <Button className="flex-1" onClick={input.handleAuthVerify} size="medium" variant="primary">
          Verify
        </Button>
      </FooterRow>
    )

  if (step === 'kyc-1') {
    if (input.kycBlocked)
      return (
        <FooterRow>
          <BackButton onClick={input.handleClearBlocked} />
          <Button
            className="flex-1"
            onClick={input.handleDepositCrypto}
            size="medium"
            variant="primary"
          >
            Deposit crypto
          </Button>
        </FooterRow>
      )
    const isLast = kycSubStep === 'address'
    return (
      <FooterRow>
        <BackButton onClick={input.handleKycBack} />
        <Button
          className="flex-1"
          onClick={isLast ? handleSubmitKyc : handleSubmitKycSubStep}
          size="medium"
          variant="primary"
        >
          Continue
        </Button>
      </FooterRow>
    )
  }

  if (step === 'kyc-2') {
    // Stage 1: notice screen. Primary CTA opens the handoff iframe;
    // the trailing ExternalLink glyph signals "you're about to leave
    // the modal for a hosted Stripe surface".
    if (kyc2Stage === 'notice')
      return (
        <FooterRow>
          <BackButton onClick={onBack} />
          <Button
            className="flex-1"
            onClick={handleVerifyDocuments}
            size="medium"
            variant="primary"
          >
            <span className="inline-flex items-center gap-1.5">
              Verify identity
              <ExternalLink aria-hidden className="size-3.5" />
            </span>
          </Button>
        </FooterRow>
      )
    // Stage 2: mocked iframe surface — single Continue advances past
    // the handoff in the playground demo. Production wouldn't render
    // this button at all (the live Stripe Identity iframe owns its
    // own success / dismiss UI); the playground keeps it so reviewers
    // have a way to step past the placeholder.
    return (
      <FooterRow>
        <Button className="flex-1" onClick={handleKyc2Continue} size="medium" variant="primary">
          Continue
        </Button>
      </FooterRow>
    )
  }

  if (step === 'wallet') {
    // Errored state shows Cancel + Retry; the in-flight state has no
    // user action available (we're waiting on the network), so we
    // suppress the footer button entirely — the CenteredStatus hero
    // in the body already communicates progress.
    if (walletErrored)
      return (
        <FooterRow>
          <BackButton onClick={onBack} />
          <Button
            className="flex-1"
            loading={walletRetrying}
            onClick={handleRetryWallet}
            size="medium"
            variant="primary"
          >
            Retry
          </Button>
        </FooterRow>
      )
    return null
  }

  if (step === 'payment' && elementKind !== 'payment') {
    // Body has no actionable form until the user has at least one
    // saved method to deposit against, so we drop the footer button
    // in the empty / loading state.
    if (savedPaymentTokens.length === 0) return null
    return (
      <FooterRow>
        <BackButton onClick={onBack} />
        <Button className="flex-1" onClick={handleStartCheckout} size="medium" variant="primary">
          Deposit
        </Button>
      </FooterRow>
    )
  }

  if (step === 'payment' && elementKind === 'payment') return null

  if (step === 'quick-confirm')
    // Single full-width primary action. The `You will add $X`
    // confirmation lives in the modal subtitle (see getStepCopy) so
    // the user reads it once, up top, before the button.
    return (
      <Button className="w-full" onClick={handleStartCheckout} size="medium" variant="primary">
        Confirm deposit
      </Button>
    )

  if (step === 'amount')
    return (
      <FooterRow>
        <Button
          className="flex-1"
          disabled={!input.canContinueAmount}
          onClick={input.handleAmountContinue}
          size="medium"
          variant="primary"
        >
          Continue
        </Button>
      </FooterRow>
    )

  if (step === 'checkout' || step === 'waiting') return null

  if (step === 'complete')
    // Surface the new wallet balance below the button stack so the
    // user closes the modal with a clear sense of "where their money
    // landed" — not just "the deposit went through". Mocked balance
    // here is `base + amount added`; production would resolve from
    // the wallet's USDC balance.
    return (
      <div className="flex flex-col gap-4">
        <FooterRow>
          <Button
            className="flex-1"
            onClick={input.handleAddMore}
            size="medium"
            variant="secondary"
          >
            Add more
          </Button>
          <Button className="flex-1" onClick={input.handleDone} size="medium" variant="primary">
            Continue
          </Button>
        </FooterRow>
        <p className="text-center text-label-12 text-foreground-secondary">
          Your balance{' '}
          <span className="font-medium tabular-nums text-foreground">
            {input.walletBalanceDisplay}
          </span>
        </p>
      </div>
    )

  if (step === 'loading') return null

  if (step === 'unavailable')
    return (
      <FooterRow>
        <Button className="flex-1" onClick={onBack} size="medium" variant="secondary">
          Close
        </Button>
        <Button
          className="flex-1"
          icon={<RotateCcw className="size-4" />}
          onClick={onChooseMethod}
          size="medium"
          variant="primary"
        >
          Try again
        </Button>
      </FooterRow>
    )

  return null
}

/**
 * Hosted-iframe placeholder used at the auth and payment-element
 * steps. Production renders the real Stripe-hosted iframe; the
 * playground swaps it for a dashed-box mock so the design footprint
 * matches what the real flow looks like.
 */
function FakeEmbeddedElement(props: {
  kind: 'auth' | 'payment'
  onComplete: () => void
  primaryLabel: string
}) {
  const { kind, onComplete, primaryLabel } = props
  return (
    <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-body border border-dashed border-border bg-pane p-6 text-center">
      <p className="text-label-13 text-foreground-secondary">
        [{kind === 'auth' ? 'Sign-in' : 'Payment-method'} iframe placeholder]
      </p>
      <Button onClick={onComplete} size="small" variant="primary">
        {primaryLabel}
      </Button>
    </div>
  )
}

/**
 * KYC-2 stage 1 — the handoff notice. Quiet info banner explaining
 * that the next click hands the user off to Stripe's secure identity
 * flow. Plain `info` palette (no lock icon — user feedback May 15:
 * the lock reads as "encrypted" not "external"; the external-link
 * glyph on the CTA carries the handoff signal far better).
 */
function Kyc2HandoffNotice() {
  return (
    <div className="-mt-1.5 flex flex-col">
      <Banner kind="info">
        Your photo ID is verified by Stripe in a secure pop-up. We never see it.
      </Banner>
    </div>
  )
}

/**
 * KYC-2 stage 2 — the mocked Stripe Identity iframe surface. Shown
 * after the user clicks `Verify identity ↗` on the notice screen.
 * Production mounts the real Stripe Identity iframe in this slot;
 * the playground swaps it for a dashed-border placeholder + a short
 * status line so reviewers can see the handoff shape end-to-end.
 * The footer's Continue button advances past this stage in the
 * mocked flow.
 */
function Kyc2IdentityIframe() {
  return (
    <div className="-mt-1.5 flex min-h-64 flex-col items-center justify-center gap-3 rounded-body border border-dashed border-border bg-pane p-6 text-center">
      <p className="font-mono text-[10px] uppercase tracking-[0.03em] text-foreground-secondary/80">
        Stripe Identity
      </p>
      <p className="text-label-13 text-foreground-secondary">
        [Identity-verification iframe placeholder]
      </p>
      <p className="text-label-12 text-foreground-secondary/70">
        In production this is replaced with the live Stripe Identity surface.
      </p>
    </div>
  )
}

/**
 * Native one-time-code auth challenge. Used when the playground
 * FlowToggle is set to `native` — replaces the hosted-iframe
 * placeholder with six individual digit slots so the auth step feels
 * like an in-modal challenge rather than a delegated handoff. Base
 * UI's `OTPField` wires up the platform `one-time-code` autofill
 * (iOS Safari, Chrome on macOS) and clipboard paste across all
 * slots, so users with an SMS/email autofill suggestion can drop
 * the code in a single tap. The Verify button in the footer
 * commits the transition; auto-complete also fires `onVerify` so
 * the flow advances as soon as the last slot is filled.
 */
function NativeAuthChallenge(props: { onVerify: () => void }) {
  const { onVerify } = props
  const [code, setCode] = useState('')
  return (
    <BaseOTPField.Root
      autoComplete="one-time-code"
      inputMode="numeric"
      length={6}
      name="link-otp"
      onValueChange={(next) => setCode(next)}
      onValueComplete={() => onVerify()}
      value={code}
    >
      <div className="grid grid-cols-6 gap-2 pt-1">
        {Array.from({ length: 6 }, (_, index) => (
          <BaseOTPField.Input
            autoFocus={index === 0}
            className="flex h-12 w-full items-center justify-center rounded-body border border-border bg-secondary text-center text-copy-17 tabular-nums text-foreground transition-colors outline-none focus-visible:border-focus focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus data-[filled]:bg-surface"
            key={index}
          />
        ))}
      </div>
    </BaseOTPField.Root>
  )
}

/**
 * Cash App / Venmo-style amount entry. Centered, oversized amount
 * with a `$` prefix and a `USD` caption beneath it; the currently
 * selected payment method renders as a tappable tile underneath so
 * the user can swap it without leaving this screen.
 */
function AmountEntry(props: {
  amount: string
  method: StripePaymentToken | undefined
  onAmountChange: (value: string) => void
  onMethodClick: () => void
}) {
  const { amount, method, onAmountChange, onMethodClick } = props
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className="flex flex-col gap-4 pt-2">
      <div
        className="flex cursor-text flex-col items-center gap-1 py-4"
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex items-baseline justify-center">
          <span className="mr-0.5 text-[36px] leading-none font-medium text-foreground-secondary">
            $
          </span>
          <input
            autoFocus
            className="bg-transparent text-[60px] leading-none font-medium tabular-nums tracking-tight text-foreground caret-foreground outline-none placeholder:text-foreground-secondary/30"
            inputMode="decimal"
            onChange={(event) => onAmountChange(formatAmountInput(event.target.value))}
            placeholder="0"
            ref={inputRef}
            // Auto-size to the value so the digit string stays
            // centered as the user types. `+0.4ch` reserves a sliver
            // of room for the blinking caret on the trailing edge.
            style={{ width: `${Math.max(amount.length, 1) + 0.4}ch` }}
            value={amount}
          />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground-secondary/80">
          USD
        </p>
      </div>
      {method && (
        <button
          className="flex items-center justify-between gap-3 rounded-body border border-border bg-pane px-3 py-2.5 text-left transition-colors duration-150 outline-none hover:bg-secondary-hover focus-visible:border-focus"
          onClick={onMethodClick}
          type="button"
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.03em] text-foreground-secondary">
              Method
            </span>
            <PaymentRow token={method} />
          </span>
          <ChevronRight aria-hidden className="size-4 shrink-0 text-foreground-secondary" />
        </button>
      )}
    </div>
  )
}

/**
 * Normalize user-typed amount input. Strips non-numeric characters
 * (except the decimal point), keeps a single decimal, caps decimal
 * places at two, and trims leading zeros so a fresh-typed `5`
 * doesn't read as `05`.
 */
function formatAmountInput(value: string): string {
  let v = value.replace(/[^\d.]/g, '')
  const firstDot = v.indexOf('.')
  if (firstDot !== -1)
    v =
      v.slice(0, firstDot + 1) +
      v
        .slice(firstDot + 1)
        .replace(/\./g, '')
        .slice(0, 2)
  if (v.length > 1 && v[0] === '0' && v[1] !== '.') v = v.replace(/^0+/, '') || '0'
  return v
}

/**
 * Render the raw amount string as `$X.XX` for display surfaces
 * (header title, deposit summary, success screen). Falls back to
 * `$0.00` for empty / unparseable input.
 */
function formatAmountDisplay(amount: string): string {
  const num = Number.parseFloat(amount)
  if (!Number.isFinite(num)) return '$0.00'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num)
}

type KycValue = {
  city: string
  dateOfBirth: string
  givenName: string
  idNumber: string
  line1: string
  line2: string
  postalCode: string
  state: string
  surname: string
}

function PaymentSelect(props: {
  invalid?: boolean
  onAddNew?: () => void
  onChange: (value: string) => void
  tokens: readonly StripePaymentToken[]
  value: string | undefined
}) {
  const { invalid, onAddNew, onChange, tokens, value } = props
  const descriptionId = useId()

  if (tokens.length === 0) return null

  const selected = tokens.find((token) => token.id === value)
  const selectedTag = selected?.card?.funding ?? selected?.us_bank_account?.account_type

  return (
    <BaseSelect.Root onValueChange={(next) => next && onChange(next)} value={value ?? ''}>
      <BaseSelect.Trigger
        aria-describedby={descriptionId}
        aria-invalid={invalid || undefined}
        className={
          // 1px border (was border-2) — matches the rest of the
          // form controls. No `METHOD` label inside the field; the
          // dropdown reads as `[icon] [•••• last4] [TAG] [▾]` with
          // the brand mark + last4 on the left and the funding
          // tag + chevron pinned to the right via `justify-between`.
          'flex h-14 w-full items-center justify-between gap-3 rounded-body border px-3.5 text-foreground transition-colors duration-150 outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus ' +
          (invalid
            ? 'border-error/60 bg-error-subtle/30 hover:bg-error-subtle/40'
            : 'border-border bg-surface hover:bg-secondary-hover')
        }
      >
        <BaseSelect.Value>
          {(next) => {
            const token = tokens.find((token) => token.id === next)
            if (!token)
              return (
                <span className="inline-flex items-center gap-2 text-label-14 text-foreground-secondary">
                  <CreditCard aria-hidden className="size-4 text-foreground-secondary" />
                  Choose a saved card or bank
                </span>
              )
            return <PaymentRow token={token} />
          }}
        </BaseSelect.Value>
        <div className="flex shrink-0 items-center gap-2">
          {selectedTag && <FundingTag>{selectedTag}</FundingTag>}
          <BaseSelect.Icon>
            <ChevronDown className="size-4 text-foreground-secondary" />
          </BaseSelect.Icon>
        </div>
      </BaseSelect.Trigger>

      <BaseSelect.Portal>
        <BaseSelect.Positioner
          alignItemWithTrigger={false}
          className="z-50 outline-none"
          sideOffset={4}
        >
          <BaseSelect.Popup className="max-h-72 min-w-[var(--anchor-width)] overflow-auto rounded-body border border-border bg-surface py-1 shadow-lg">
            {tokens.map((token) => {
              const tag = token.card?.funding ?? token.us_bank_account?.account_type
              return (
                <BaseSelect.Item
                  className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-label-14 text-foreground outline-none data-highlighted:bg-secondary-hover"
                  key={token.id}
                  value={token.id}
                >
                  <BaseSelect.ItemText>
                    <PaymentRow token={token} />
                  </BaseSelect.ItemText>
                  {tag && <FundingTag>{tag}</FundingTag>}
                </BaseSelect.Item>
              )
            })}
            {/*
              `Add another` lives at the bottom of the popup so it
              reads as an option in the list, not a competing
              action above the field. Rendered outside `BaseSelect.Item`
              so it doesn't try to set a value.
            */}
            {onAddNew && (
              <>
                <div aria-hidden className="my-1 h-px bg-border" />
                <button
                  className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-label-14 text-accent outline-none hover:bg-secondary-hover focus-visible:bg-secondary-hover"
                  onClick={onAddNew}
                  type="button"
                >
                  <span aria-hidden className="text-[14px] leading-none">
                    +
                  </span>
                  Add another method
                </button>
              </>
            )}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}

/**
 * Compact payment row for a single saved method. Brand mark sits in
 * a fixed-width slot so cards (variable-width SVG wordmarks) and
 * bank accounts (uniform glyph) share the same lead-in width and
 * the `•••• last4` text stays column-aligned across rows.
 *
 * The bullet glyph `•` has a high vertical position in most fonts
 * (it sits closer to upper-center than baseline), so without a tiny
 * `pt` nudge the digits + bullets group visually floats a couple
 * pixels above the brand mark and any right-side companions
 * (funding tag, chevron). `pt-[2px]` pulls it back to the optical
 * row middle without changing the layout height.
 */
function PaymentRow(props: { token: StripePaymentToken }) {
  const { token } = props
  if (token.card) {
    const brand = token.card.brand ?? null
    const last4 = token.card.last4 ?? '••••'
    return (
      <span className="inline-flex items-center gap-2.5 pt-[2px] text-label-14 text-foreground">
        <span className="inline-flex w-7 shrink-0 items-center justify-center">
          <CardBrandIcon brand={brand} className="h-4 w-auto" />
        </span>
        <span className="tabular-nums leading-none">{`\u2022\u2022\u2022\u2022 ${last4}`}</span>
      </span>
    )
  }
  if (token.us_bank_account) {
    const last4 = token.us_bank_account.last4
    return (
      <span className="inline-flex items-center gap-2.5 pt-[2px] text-label-14 text-foreground">
        <span className="inline-flex w-7 shrink-0 items-center justify-center">
          <Building aria-hidden className="size-4 text-foreground-secondary" />
        </span>
        <span className="tabular-nums leading-none">{`\u2022\u2022\u2022\u2022 ${last4 ?? '••••'}`}</span>
      </span>
    )
  }
  return <span className="text-label-14 text-foreground">{toTitleCase(token.type)}</span>
}

/** Mono-font funding-source tag (`credit` / `debit` / `checking`). */
function FundingTag(props: { children: ReactNode }) {
  return (
    <span className="rounded-button border border-border bg-surface px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-foreground-secondary">
      {props.children}
    </span>
  )
}

/**
 * Quick-confirm lockup card — the returning-user one-tap-pay UX.
 * Renders a nested card with:
 *
 *   ┌────────────────────────────────────┐
 *   │ ● link  jane@example.com           │
 *   │                                    │
 *   │ ┌───────────────────────────────┐  │
 *   │ │ VISA  Visa Credit             │  │
 *   │ │       •••• 4242        Change │  │
 *   │ └───────────────────────────────┘  │
 *   └────────────────────────────────────┘
 *
 * The Frame footer renders the primary `Deposit $X` CTA below.
 * Modeled on the Stripe Link one-tap-pay card seen at `link.com/pay`
 * (linked from the May 14 spec) — the user is signed in, KYC is
 * cached, the card is on file, and the confirm becomes a single tap.
 */
function QuickConfirmLockup(props: {
  email: string
  error: string | undefined
  onAddNewMethod: () => void
  onChangeAccount: () => void
  onPaymentTokenChange: (next: string) => void
  paymentToken: string | undefined
  tokens: readonly StripePaymentToken[]
}) {
  const {
    email,
    error,
    onAddNewMethod,
    onChangeAccount,
    onPaymentTokenChange,
    paymentToken,
    tokens,
  } = props
  const friendly = error ? humanizeStripeError(error) : undefined
  return (
    <div className="flex flex-col gap-3 rounded-body border border-border bg-pane p-3">
      <div className="flex items-center justify-between gap-2">
        {/*
          Email-as-anchor on the left. The Link mark is on the right
          side now (next to the `⋯`) so the email reads as the
          account identifier, not part of the Link wordmark.
        */}
        <span className="min-w-0 truncate text-label-13 text-foreground">{email}</span>
        <div className="inline-flex shrink-0 items-center gap-0.5">
          {/*
            Dimmed Link mark — affordance for "this account lives at
            link.com". Bright on hover so the user can still discover
            it without it dominating the row.
          */}
          <a
            aria-label="Open your Stripe Link account in a new tab"
            className="inline-flex size-6 items-center justify-center rounded-button text-foreground-secondary/35 transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
            href="https://link.com"
            rel="noreferrer noopener"
            target="_blank"
          >
            <LinkMark className="h-3.5" />
          </a>
          {/*
            Quiet `⋯` menu — opens a small popover with the two
            account-level options that don't belong inline in the
            payment dropdown below. `Add new method` routes into the
            hosted payment-element collection step; `Switch account`
            routes back to the email-entry preauth step preserving
            the rest of the deposit state.
          */}
          <BaseMenu.Root>
            <BaseMenu.Trigger
              aria-label="More options"
              className="inline-flex size-6 items-center justify-center rounded-full text-foreground-secondary/60 hover:bg-secondary-hover hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
            >
              <span aria-hidden className="text-[14px] leading-none">
                ⋯
              </span>
            </BaseMenu.Trigger>
            <BaseMenu.Portal>
              <BaseMenu.Positioner align="end" className="z-50 outline-none" sideOffset={6}>
                <BaseMenu.Popup className="min-w-[160px] overflow-hidden rounded-body border border-border bg-surface py-1 shadow-lg">
                  <BaseMenu.Item
                    className="cursor-pointer px-3 py-2 text-label-13 text-foreground outline-none data-highlighted:bg-secondary-hover"
                    onClick={onAddNewMethod}
                  >
                    Add new method
                  </BaseMenu.Item>
                  <BaseMenu.Item
                    className="cursor-pointer px-3 py-2 text-label-13 text-foreground outline-none data-highlighted:bg-secondary-hover"
                    onClick={onChangeAccount}
                  >
                    Switch account
                  </BaseMenu.Item>
                </BaseMenu.Popup>
              </BaseMenu.Positioner>
            </BaseMenu.Portal>
          </BaseMenu.Root>
        </div>
      </div>
      <PaymentSelect
        invalid={!!error}
        onAddNew={onAddNewMethod}
        onChange={onPaymentTokenChange}
        tokens={tokens}
        value={paymentToken}
      />
      {friendly && <FieldErrorMessage>{friendly}</FieldErrorMessage>}
    </div>
  )
}

/**
 * Slim opt-in row that sits under the saved-methods select. Checking
 * it persists the user's "skip the chain next time" preference so the
 * next deposit from this device lands on the `quick-confirm` step
 * (see {@link QuickConfirmLockup}). Visual-only in the playground.
 */
function RememberMethodToggle(props: { checked: boolean; onChange: (next: boolean) => void }) {
  const { checked, onChange } = props
  const id = useId()
  // Track always uses `border border-border` (1px, same as the
  // dropdown above) so the toggle reads as part of the same
  // control family. The knob is `bg-foreground` (white in dark
  // mode, black in light mode) — full opacity when active so it
  // pops against the filled track, dimmed when inactive so it
  // reads as a quiet hint sitting in the empty track. Active
  // state also fills the track interior with a subtle
  // `bg-foreground/15` so the on/off contrast is unmistakable
  // without a high-saturation accent colour.
  return (
    <label
      className="mt-1 flex w-full cursor-pointer items-center justify-between gap-3 text-label-13 text-foreground-secondary hover:text-foreground"
      htmlFor={id}
    >
      <span className="flex-1">Remember this card for next time</span>
      <span className="relative inline-block h-[22px] w-[36px] shrink-0">
        <input
          checked={checked}
          className="peer sr-only"
          id={id}
          onChange={(event) => onChange(event.target.checked)}
          type="checkbox"
        />
        <span
          aria-hidden
          className={
            'absolute inset-0 block rounded-full border border-border transition-colors duration-200 ' +
            (checked ? 'bg-foreground/15' : 'bg-surface')
          }
        />
        <span
          aria-hidden
          className={
            'absolute top-[3px] block size-[14px] rounded-full bg-foreground transition-all duration-200 ' +
            (checked ? 'left-[19px] opacity-100' : 'left-[3px] opacity-50')
          }
        />
      </span>
    </label>
  )
}

/**
 * Persistent identity strip at the bottom of the modal once the user
 * is signed in. Renders as a slim inset card with the user's email
 * left-aligned and a single external-link affordance that opens the
 * Link account dashboard in a new tab.
 */
/**
 * Bottom-of-modal footer band — the post-auth identity (email) sits
 * on the left, with a help affordance and the provider lockup on
 * the right. Renders as an inset band with a divider above so it
 * reads as a true page-footer, not a nested floating card. Negative
 * margins push the band to the modal edges, consuming the Frame's
 * footer padding so the inset color spans the full width. The
 * `provider` prop swaps the right-side lockup between the Link mark
 * (production default) and the Stripe wordmark.
 */
function OnrampFooterRow(props: {
  disableHelp: boolean
  email?: string | undefined
  provider?: FooterProvider
}) {
  const { disableHelp, email, provider = 'link' } = props
  const providerHref = provider === 'link' ? 'https://link.com' : 'https://stripe.com'
  const helpHref = HELP_URL_BY_PROVIDER[provider]
  const providerLabel =
    provider === 'link' ? 'Open Link account in a new tab' : 'Learn more about Stripe'
  return (
    <div className="-mx-5 -mb-3 mt-3 flex items-center justify-between gap-2 border-t border-border px-5 pt-3 pb-2">
      {email ? (
        <span className="min-w-0 truncate text-label-13 text-foreground-secondary">{email}</span>
      ) : (
        <span aria-hidden />
      )}
      <div className="flex shrink-0 items-center gap-1">
        {/*
          Provider mark first (left of help), so the user reads
          `email — link — ?` as `who am I -> who's processing this ->
          where can I get help`. The reverse felt wrong because the
          provider mark *anchors* the strip; help is an escape
          hatch.
        */}
        <a
          aria-label={providerLabel}
          className="group inline-flex items-center rounded-button px-1 py-0.5 text-foreground-secondary/70 transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
          href={providerHref}
          rel="noreferrer noopener"
          target="_blank"
        >
          {provider === 'link' ? (
            <LinkMark className="h-[18px]" />
          ) : (
            <StripeWordmark className="h-[18px]" />
          )}
        </a>
        {disableHelp ? (
          <span
            aria-hidden
            className="inline-flex size-6 items-center justify-center text-foreground-secondary/30"
          >
            <HelpCircle aria-hidden className="size-3.5" />
          </span>
        ) : (
          <a
            aria-label={
              provider === 'link' ? 'Open Stripe Link FAQs' : 'Open Stripe Crypto support'
            }
            className="inline-flex size-6 items-center justify-center rounded-button text-foreground-secondary/60 hover:bg-surface-hover hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
            href={helpHref}
            rel="noreferrer noopener"
            target="_blank"
          >
            <HelpCircle aria-hidden className="size-3.5" />
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * Stripe wordmark, sourced from Stripe's brand assets. Inlined as a
 * component so it inherits `currentColor` from the surrounding text
 * and tracks the muted → foreground hover transition without an
 * extra image asset.
 */
function StripeWordmark(props: { className?: string }) {
  return (
    <svg
      aria-hidden
      className={props.className}
      fill="currentColor"
      role="img"
      viewBox="0 0 60 25"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 9.01c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.58-.24 1.58-1C6.32 13.84 0 14.43 0 9.66 0 6.78 2.24 5 5.65 5c1.95 0 3.9.34 5.86 1.13v3.66c-1.79-.94-3.14-1.46-4.79-1.46-.86 0-1.4.24-1.4.95 0 1.6 6.25 1.21 6.25 5.35 0 .29-.02.61-.07.95z" />
    </svg>
  )
}

/**
 * Link-by-Stripe lockup with a muted + full-color crossfade.
 *
 * Renders two stacked SVGs:
 *
 * - The base layer is a single-color silhouette — the badge is filled
 *   with `currentColor` and the chevron is punched out as a mask
 *   cutout (so the modal surface shows through the arrow shape). The
 *   wordmark also uses `currentColor`. The whole thing reads as a
 *   greyscale lockup at the parent's text color.
 * - The hover layer is the original brand-color lockup (green badge,
 *   white chevron, wordmark in `currentColor`).
 *
 * On `group-hover` the base fades out and the brand layer fades in.
 * Drop this inside an element with the `group` class to wire the
 * hover interaction.
 */
function LinkMark(props: { className?: string }) {
  const { className } = props
  const maskId = useId()
  return (
    <span className={'relative inline-block aspect-[60/22] ' + (className ?? 'h-3.5')}>
      {/* Single-color silhouette: cutout chevron, currentColor everywhere else. */}
      <svg
        aria-hidden
        className="absolute inset-0 h-full w-full transition-opacity duration-150 group-hover:opacity-0"
        role="img"
        viewBox="0 0 60 22"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id={maskId}>
            <rect fill="white" height="22" rx="11" width="22" x="0" y="0" />
            <path
              d="M7.5 5.5 L14 11 L7.5 16.5"
              fill="none"
              stroke="black"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
            />
          </mask>
        </defs>
        <rect
          fill="currentColor"
          height="22"
          mask={`url(#${maskId})`}
          rx="11"
          width="22"
          x="0"
          y="0"
        />
        <text
          dominantBaseline="middle"
          fill="currentColor"
          fontFamily="-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
          fontSize="18"
          fontWeight="800"
          letterSpacing="-0.04em"
          x="26"
          y="13"
        >
          link
        </text>
      </svg>
      {/* Brand-color overlay: hidden by default, fades in on hover. */}
      <svg
        aria-hidden
        className="absolute inset-0 h-full w-full opacity-0 transition-opacity duration-150 group-hover:opacity-100"
        role="img"
        viewBox="0 0 60 22"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect fill="#00D66F" height="22" rx="11" width="22" x="0" y="0" />
        <path
          d="M7.5 5.5 L14 11 L7.5 16.5"
          fill="none"
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3"
        />
        <text
          dominantBaseline="middle"
          fill="currentColor"
          fontFamily="-apple-system, BlinkMacSystemFont, system-ui, sans-serif"
          fontSize="18"
          fontWeight="800"
          letterSpacing="-0.04em"
          x="26"
          y="13"
        >
          link
        </text>
      </svg>
    </span>
  )
}

/**
 * Inline modal header — replaces Frame's built-in title/subtitle. The
 * tight `gap-1` between heading and subtitle is what the redesign
 * calls for, and the dismiss X has no resting background (only a
 * hover affordance) to keep the chrome quiet. Top padding is `pt-0`
 * when StepDots sit above this header (the dots wrapper already
 * owns the top inset) and `pt-5` otherwise so the title's top
 * margin visually matches the body's `pb-4` bottom margin on the
 * first-step / hero screens that don't render dots.
 */
function ModalHeader(props: {
  compactTop?: boolean
  onDismiss: () => void
  subtitle?: ReactNode
  title: string
}) {
  const { compactTop = false, onDismiss, subtitle, title } = props
  // Title always gets breathing room — `pt-3` is the floor on the
  // non-compact path, `pt-2` when StepDots sit above us (and have
  // their own bottom padding). Zero-padding is never allowed
  // because the title visually crashes into the modal's top edge
  // (Brendan, May 19).
  return (
    <div className={'flex flex-col gap-1 ' + (compactTop ? 'pt-2' : 'pt-3')}>
      <div className="flex items-center">
        <h2 className="flex-1 text-heading-20 text-foreground">{title}</h2>
        <button
          aria-label="Dismiss"
          className="-mr-2 -mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-button text-foreground-secondary hover:bg-surface-hover hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
          onClick={onDismiss}
          type="button"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {subtitle && (
        <p className="flex flex-wrap items-center gap-x-1 text-label-13 text-foreground-secondary">
          {subtitle}
        </p>
      )}
    </div>
  )
}

/**
 * Floating top-right dismiss for steps where the body owns a centered
 * status hero (loading, in-flight, success, error). Same hover-only
 * background as the inline ModalHeader X.
 */
function FloatingDismiss(props: { onClick: () => void }) {
  return (
    <button
      aria-label="Dismiss"
      className="absolute right-3 top-3 z-10 inline-flex size-8 items-center justify-center rounded-button text-foreground-secondary hover:bg-surface-hover hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
      onClick={props.onClick}
      type="button"
    >
      <X aria-hidden className="size-4" />
    </button>
  )
}

/**
 * Step indicator. Pure dots, no inline labels, anchored at the top of
 * the modal. The active step's dot stretches into a pill so progress
 * is legible at a glance — Apple-style pagination dots. The label is
 * exposed via aria for screen readers.
 */
function StepDots(props: { current: StepIndex }) {
  const { current } = props
  return (
    <ol
      aria-label={`Step ${current} of ${STEP_LABELS.length}: ${STEP_LABELS[current - 1]}`}
      className="flex items-center justify-center gap-1.5"
    >
      {STEP_LABELS.map((label, i) => {
        const idx = (i + 1) as StepIndex
        const isActive = idx === current
        const isDone = idx < current
        return (
          <li key={label}>
            <span
              aria-current={isActive || undefined}
              aria-label={label}
              className={
                'block h-1.5 rounded-full transition-all duration-200 ' +
                (isActive
                  ? 'w-5 bg-foreground'
                  : isDone
                    ? 'w-1.5 bg-foreground/55'
                    : 'w-1.5 bg-foreground/20')
              }
            />
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Floating-label input — label nests inside the field above the input
 * placeholder so a column of fields stays visually quiet. Slightly
 * taller (52px) than the regular `<Input>` to fit two lines.
 */
const NestedField = forwardRef<HTMLInputElement, NestedFieldProps>(
  function NestedField(props, ref) {
    const { className, inputClassName, invalid, label, suffix, ...rest } = props
    const id = useId()
    return (
      <label
        className={
          // Grid lets the suffix slot span the full height (label +
          // input) and `items-center` then vertically centers any
          // calendar / eye / clear button within that span. `pt-2
          // pb-1.5` keeps the label-to-input rhythm visually even.
          'grid grid-cols-[1fr_auto] items-center gap-x-2 rounded-body border px-3 pt-2 pb-1.5 transition-colors duration-150 [&_input::-webkit-calendar-picker-indicator]:opacity-0' +
          (invalid
            ? ' border-error/40 bg-error-subtle/30 hover:bg-error-subtle/40 focus-within:border-error/70'
            : ' border-transparent bg-secondary hover:bg-secondary-hover focus-within:border-focus focus-within:bg-secondary-hover') +
          (className ? ` ${className}` : '')
        }
        htmlFor={id}
      >
        <span className="flex min-w-0 flex-col gap-1">
          <span
            className={
              'font-mono text-[10px] leading-none uppercase tracking-[0.03em] ' +
              (invalid ? 'text-error' : 'text-foreground-secondary')
            }
          >
            {label}
          </span>
          <input
            className={
              'min-w-0 bg-transparent leading-tight text-foreground outline-none placeholder:text-foreground-secondary/50 ' +
              (inputClassName ? inputClassName : 'text-copy-14')
            }
            id={id}
            ref={ref}
            {...rest}
          />
        </span>
        {suffix && <span className="shrink-0">{suffix}</span>}
      </label>
    )
  },
)

type NestedFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /** Optional class for the inner `<input>` (e.g. for size overrides). */
  inputClassName?: string
  /** When true, paints the field with the error palette. */
  invalid?: boolean
  /** Floating label text. */
  label: ReactNode
  /** Optional trailing element (icon button, eye toggle, calendar, etc.). */
  suffix?: ReactNode
}

/**
 * Section grouping for a column of `NestedField`s with a single
 * uppercase title — replaces individual external labels.
 */
function FormSection(props: { children: ReactNode; title?: string }) {
  const { children, title } = props
  return (
    <div className="flex flex-col gap-2">
      {title && (
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-foreground-secondary">
          {title}
        </p>
      )}
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  )
}

/**
 * Centered status hero — used for every loading/error/success state.
 * Pairs a translucent-background icon with a heading-styled title and
 * label-styled subtitle so the visual rhythm matches the inline
 * modal header (`ModalHeader`).
 */
/**
 * Greeked-out skeleton of the upcoming payment-picker body. Rendered
 * underneath the `wallet`-linking blur overlay so the user sees a
 * faint preview of their next screen, not a blank modal. Visual-only
 * — no interactivity, no real data dependencies.
 */
function PaymentPickerPreview() {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="h-3 w-16 rounded bg-secondary" />
        <span className="h-3 w-20 rounded bg-secondary" />
      </div>
      <div className="flex h-12 w-full items-center justify-between rounded-body border border-border bg-surface px-3">
        <span className="inline-flex items-center gap-2.5">
          <span className="h-4 w-7 rounded bg-secondary" />
          <span className="h-3 w-24 rounded bg-secondary" />
        </span>
        <span className="size-4 rounded bg-secondary" />
      </div>
      <div className="h-3 w-48 rounded bg-secondary/60" />
    </div>
  )
}

function CenteredStatus(props: {
  kind: 'loading' | 'error' | 'success'
  title: string
  subtitle?: ReactNode
}) {
  const { kind, subtitle, title } = props
  const iconWrapperClass =
    kind === 'error'
      ? 'bg-negative-subtle text-negative'
      : kind === 'success'
        ? 'bg-positive-subtle text-positive'
        : 'bg-secondary text-foreground-secondary'
  return (
    <div aria-live="polite" className="flex flex-col items-center gap-3 px-4 py-4 text-center">
      <span
        className={
          'inline-flex size-10 items-center justify-center rounded-full ' + iconWrapperClass
        }
      >
        {kind === 'loading' && <LoaderCircle aria-hidden className="size-5 animate-rgn-spin" />}
        {kind === 'error' && <AlertTriangle aria-hidden className="size-5" />}
        {kind === 'success' && <CheckCircle aria-hidden className="size-5" />}
      </span>
      {/*
        The title + subtitle are keyed by their content so React
        unmounts/remounts them on every copy change. Paired with the
        `motion-safe:animate-stripe-fade-in` keyframe, that gives a
        smooth fade-in on each step transition (`checkout` → `waiting`
        → `complete`) — Brendan: "I can't read the processing state".
      */}
      <div
        className="flex flex-col gap-1 motion-safe:animate-stripe-fade-in"
        key={`${title}|${String(subtitle ?? '')}`}
      >
        <h3 className="text-heading-20 text-foreground">{title}</h3>
        {subtitle && <p className="text-label-13 text-foreground-secondary">{subtitle}</p>}
      </div>
    </div>
  )
}

/**
 * First-step picker — pick the onramp provider. Stripe (cards / Apple
 * Pay) is the only live path today; the row stub for "Other providers"
 * gives the user a sense that this is a choice, not a forced flow.
 */
function MethodPicker(props: { onPick: (method: 'stripe' | 'other') => void }) {
  const { onPick } = props
  // Two-line title/subtitle stacks use `leading-tight` + `gap-0.5`
  // so the optical center of the text column sits flush with the
  // icon circle. Without it, default line-height pushes the
  // baseline down ~3px and the icon floats high.
  return (
    <div className="flex flex-col gap-2">
      <button
        className="group flex items-center gap-3 rounded-body border border-border bg-pane px-4 py-3 text-left transition-colors hover:border-foreground/30 hover:bg-secondary-hover focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
        onClick={() => onPick('stripe')}
        type="button"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-surface text-foreground ring-1 ring-border">
          <CreditCard aria-hidden className="size-4" />
        </span>
        <span className="flex flex-1 flex-col gap-0.5 leading-tight">
          <span className="text-copy-14 text-foreground">Pay with card</span>
          <span className="text-label-13 text-foreground-secondary">Works with Apple Pay</span>
        </span>
        <ChevronRight aria-hidden className="size-4 shrink-0 text-foreground-secondary" />
      </button>
      <button
        aria-disabled
        className="group flex cursor-not-allowed items-center gap-3 rounded-body border border-border bg-pane px-4 py-3 text-left opacity-70"
        disabled
        type="button"
      >
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground-secondary">
          <Building aria-hidden className="size-4" />
        </span>
        <span className="flex flex-1 flex-col gap-0.5 leading-tight">
          <span className="text-copy-14 text-foreground">Transfer from bank</span>
          <span className="text-label-13 text-foreground-secondary">
            Available in 36+ countries
          </span>
        </span>
        <span className="shrink-0 rounded-button border border-border bg-surface px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-foreground-secondary">
          Soon
        </span>
      </button>
    </div>
  )
}

function Banner(props: {
  kind: 'error' | 'info' | 'success'
  children: ReactNode
  icon?: ReactNode
}) {
  const { children, icon, kind } = props
  const styles = {
    error: 'border-error bg-error-subtle text-error',
    info: 'border-info bg-info-subtle text-info',
    success: 'border-success bg-success-subtle text-success',
  } as const
  return (
    <div
      className={`flex items-center justify-center gap-2.5 rounded-body border px-3 py-2 text-center text-label-13 ${styles[kind]}`}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      {icon && (
        <span aria-hidden className="shrink-0">
          {icon}
        </span>
      )}
      <span className="min-w-0">{children}</span>
    </div>
  )
}

/**
 * Compact "back" affordance — icon-only square button that takes
 * about ~15% of the footer width. Frees up horizontal space for the
 * primary CTA which now reads as the dominant action on every
 * two-button row.
 */
function BackButton(props: { onClick: () => void }) {
  // Square 1:1 aspect ratio so the back-arrow icon sits centered in
  // a symmetric tap target. The default `medium` Button is wider
  // than tall; `w-10 h-10 aspect-square` overrides both.
  return (
    <Button
      aria-label="Go back"
      className="aspect-square h-10 w-10 px-0"
      icon={<ArrowLeft aria-hidden className="size-4" />}
      onClick={props.onClick}
      size="medium"
      variant="secondary"
    />
  )
}

/**
 * OTP resend button with its own state machine.
 *
 *   `idle` → user clicks → `sending` (icon spins, button dims and is
 *   non-interactive) → on completion → `cooldown` (button shows
 *   `Wait 00:25` countdown, locked out for 25 s) → ticks to 0 →
 *   `idle`.
 *
 * The countdown digits + the trailing `s` unit are rendered in a
 * monospace font so the button width never shifts as the seconds
 * tick down (otherwise the proportional digits resize the row each
 * second). The icon uses `animate-rgn-spin` while sending — same
 * keyframe the global loader uses.
 */
function ResendButton(props: {
  className?: string
  cooldownSeconds?: number
  onResend: () => void
}) {
  const { className, cooldownSeconds = 25, onResend } = props
  const [state, setState] = useState<'idle' | 'sending' | 'cooldown'>('idle')
  const [remaining, setRemaining] = useState(0)

  useEffect(() => {
    if (state !== 'cooldown') return
    if (remaining <= 0) {
      setState('idle')
      return
    }
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000)
    return () => clearTimeout(id)
  }, [state, remaining])

  const handleClick = () => {
    if (state !== 'idle') return
    setState('sending')
    onResend()
    // Simulate the network round-trip in the playground. Production
    // would await the actual mutation and transition on settle.
    setTimeout(() => {
      setRemaining(cooldownSeconds)
      setState('cooldown')
    }, 700)
  }

  const formatted = (() => {
    const mm = String(Math.floor(remaining / 60)).padStart(2, '0')
    const ss = String(remaining % 60).padStart(2, '0')
    return `${mm}:${ss}`
  })()

  return (
    <Button
      aria-label={
        state === 'sending'
          ? 'Sending code'
          : state === 'cooldown'
            ? `Wait ${formatted} before requesting another code`
            : 'Resend code'
      }
      className={className}
      disabled={state !== 'idle'}
      icon={
        <RotateCcw
          aria-hidden
          className={
            'size-4 transition-opacity duration-150 ' +
            (state === 'sending' ? 'animate-rgn-spin opacity-70' : 'opacity-100')
          }
        />
      }
      onClick={handleClick}
      size="medium"
      variant="secondary"
    >
      {state === 'sending' && <span className="opacity-60">Sending</span>}
      {state === 'cooldown' && (
        <span className="inline-flex items-baseline gap-1">
          <span>Wait</span>
          <span className="font-mono tabular-nums tracking-tight">{formatted}</span>
        </span>
      )}
      {state === 'idle' && <span>Resend</span>}
    </Button>
  )
}

/**
 * Two-up footer row. Reserved for back + primary CTA pairs where
 * the back button is icon-only and the primary stretches across
 * the remaining width.
 */
function FooterRow(props: { children: ReactNode }) {
  return <div className="flex gap-3">{props.children}</div>
}

/**
 * Field-level error message — sits directly under the input(s) it
 * describes. Compact (no icon, no card chrome) so it reads as a
 * quiet note on the field above, not a competing banner. Pair with
 * an `invalid`-painted input or trigger above for the full "what
 * went wrong" pattern.
 */
function FieldErrorMessage(props: { children: ReactNode }) {
  return (
    <div
      className="rounded-body border border-negative bg-negative-subtle px-3 py-2 text-label-13 text-negative"
      role="alert"
    >
      {props.children}
    </div>
  )
}

/**
 * KYC sub-flow — 2 sub-steps (identity + address). Identity carries
 * First name / Last name / DOB / SSN with the no-SSN -> ID upload
 * fork and the no-ID -> blocked + crypto-fallback fork. Address
 * uses missed-field reds (no per-field validation rows). Matches the
 * production data requirements (legal name, DOB, SSN, US address).
 */
function KycSubFlow(props: {
  attempted: boolean
  kyc: KycValue
  onBlock: (kind: 'no-address' | 'no-id') => void
  onChange: (next: Partial<KycValue>) => void
  subStep: KycSubStep
}) {
  const { attempted, kyc, onBlock, onChange, subStep } = props
  if (subStep === 'address')
    return (
      <KycAddressSubStep attempted={attempted} kyc={kyc} onBlock={onBlock} onChange={onChange} />
    )
  return (
    <KycIdentitySubStep attempted={attempted} kyc={kyc} onBlock={onBlock} onChange={onChange} />
  )
}

function KycIdentitySubStep(props: {
  attempted: boolean
  kyc: KycValue
  onBlock: (kind: 'no-address' | 'no-id') => void
  onChange: (next: Partial<KycValue>) => void
}) {
  const { attempted, kyc, onBlock, onChange } = props
  const dobRef = useRef<HTMLInputElement | null>(null)
  const [noSsn, setNoSsn] = useState(false)
  // Default the SSN field to masked so a casual onlooker doesn't see
  // the number when the user happens to step away. The eye toggle
  // flips between formatted-dash digits (`123-45-6789`) and bullet
  // placeholders (`•••-••-••••`) while preserving the same group
  // separators so the field's footprint is stable.
  const [ssnVisible, setSsnVisible] = useState(false)
  const missGivenName = attempted && kyc.givenName.trim() === ''
  const missSurname = attempted && kyc.surname.trim() === ''
  const missDob = attempted && !kyc.dateOfBirth
  const missSsn = attempted && !noSsn && kyc.idNumber.replace(/\D/g, '').length !== 9
  // Per-field validation rendering: when only one field is invalid
  // we show the red banner directly under that field; otherwise the
  // banner sits at the form bottom listing everything that's missing
  // in one breath. Inline placement makes single-issue fixes obvious;
  // the consolidated message keeps multi-issue forms from being
  // flooded with stacked banners.
  const invalidKeys = [
    missGivenName ? 'givenName' : null,
    missSurname ? 'surname' : null,
    missDob ? 'dob' : null,
    missSsn ? 'ssn' : null,
  ].filter(Boolean) as readonly ('givenName' | 'surname' | 'dob' | 'ssn')[]
  const single = invalidKeys.length === 1 ? invalidKeys[0] : undefined
  const singleMessage = (() => {
    if (single === 'givenName') return 'Enter your first name.'
    if (single === 'surname') return 'Enter your last name.'
    if (single === 'dob') return 'Enter your date of birth.'
    if (single === 'ssn') return 'Enter your 9-digit Social Security Number.'
    return undefined
  })()
  const multiMessage =
    invalidKeys.length > 1 ? 'Fill in the missing fields to continue.' : undefined

  return (
    // `-mt-1.5` pulls the first row up under the Frame title so the
    // identity sub-step opens flush with the subtitle instead of
    // floating in a band of whitespace above the First / Last row.
    <div className="-mt-1.5 flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-x-2 gap-y-2">
        <NestedField
          autoComplete="given-name"
          autoFocus
          invalid={missGivenName}
          label="First name"
          name="given-name"
          onChange={(event) => onChange({ givenName: event.target.value })}
          placeholder="Jane"
          type="text"
          value={kyc.givenName}
        />
        <NestedField
          autoComplete="family-name"
          invalid={missSurname}
          label="Last name"
          name="family-name"
          onChange={(event) => onChange({ surname: event.target.value })}
          placeholder="Doe"
          type="text"
          value={kyc.surname}
        />
        {(single === 'givenName' || single === 'surname') && (
          <div className="col-span-2">
            <FieldErrorMessage>{singleMessage}</FieldErrorMessage>
          </div>
        )}
      </div>
      <NestedField
        autoComplete="bday"
        invalid={missDob}
        label="Date of birth"
        name="bday"
        onChange={(event) => {
          onChange({ dateOfBirth: event.target.value })
        }}
        placeholder="mm/dd/yyyy"
        ref={dobRef}
        suffix={
          <IconSuffixButton
            aria-label="Open calendar"
            icon={<Calendar aria-hidden className="size-3.5" />}
            onClick={() => {
              const el = dobRef.current as (HTMLInputElement & { showPicker?: () => void }) | null
              if (el && typeof el.showPicker === 'function') el.showPicker()
              else el?.focus()
            }}
          />
        }
        type="date"
        value={kyc.dateOfBirth}
      />
      {single === 'dob' && <FieldErrorMessage>{singleMessage}</FieldErrorMessage>}
      {!noSsn && (
        <NestedField
          autoComplete="off"
          data-1p-ignore="true"
          data-form-type="other"
          data-lpignore="true"
          inputClassName={
            // Mask the digits visually via CSS so the underlying value
            // stays the real formatted SSN. Stripping bullets out of
            // `event.target.value` in `onChange` lost prior digits
            // and capped the user to a single typed character.
            'text-copy-14 tabular-nums tracking-[0.04em]' +
            (ssnVisible ? '' : ' [-webkit-text-security:disc] [text-security:disc]')
          }
          inputMode="numeric"
          invalid={missSsn}
          label="Social Security Number"
          maxLength={11}
          name="ssn"
          onChange={(event) => onChange({ idNumber: formatSsnDash(event.target.value) })}
          placeholder="123-45-6789"
          spellCheck={false}
          suffix={
            <IconSuffixButton
              aria-label={
                ssnVisible ? 'Hide Social Security Number' : 'Show Social Security Number'
              }
              aria-pressed={ssnVisible}
              icon={
                ssnVisible ? (
                  <EyeOff aria-hidden className="size-3.5" />
                ) : (
                  <Eye aria-hidden className="size-3.5" />
                )
              }
              onClick={() => setSsnVisible((v) => !v)}
            />
          }
          type="text"
          value={kyc.idNumber}
        />
      )}
      {single === 'ssn' && <FieldErrorMessage>{singleMessage}</FieldErrorMessage>}
      {noSsn && <IdUploadDropZone />}
      <Checkbox
        checked={noSsn}
        label="I don't have an SSN"
        onChange={() => {
          setNoSsn((v) => !v)
        }}
      />
      {noSsn && (
        <Checkbox
          checked={false}
          label="I don't have a government-issued ID either"
          onChange={() => onBlock('no-id')}
        />
      )}
      {multiMessage && <FieldErrorMessage>{multiMessage}</FieldErrorMessage>}
    </div>
  )
}

function KycAddressSubStep(props: {
  attempted: boolean
  kyc: KycValue
  onBlock: (kind: 'no-address' | 'no-id') => void
  onChange: (next: Partial<KycValue>) => void
}) {
  const { attempted, kyc, onBlock, onChange } = props
  const [poBox, setPoBox] = useState(false)
  const missedLine1 = attempted && kyc.line1.trim() === ''
  const missedCity = attempted && kyc.city.trim() === ''
  const missedState = attempted && !kyc.state
  const missedZip = attempted && !/^\d{5}(-\d{4})?$/.test(kyc.postalCode.trim())
  const invalidKeys = [
    missedLine1 ? 'line1' : null,
    missedCity ? 'city' : null,
    missedState ? 'state' : null,
    missedZip ? 'zip' : null,
  ].filter(Boolean) as readonly ('line1' | 'city' | 'state' | 'zip')[]
  const single = invalidKeys.length === 1 ? invalidKeys[0] : undefined
  const singleMessage = (() => {
    if (single === 'line1') return 'Enter your street address.'
    if (single === 'city') return 'Enter your city.'
    if (single === 'state') return 'Pick your state.'
    if (single === 'zip') return 'Enter a valid 5-digit ZIP.'
    return undefined
  })()
  const multiMessage =
    invalidKeys.length > 1 ? 'Fill in the missing address fields to continue.' : undefined

  return (
    // `-mt-1.5` pulls the first row up under the Frame title so the
    // address form doesn't open with a noticeably wide band of
    // whitespace above the Street row — the Frame header already
    // owns 16px of bottom padding under the subtitle.
    <div className="-mt-1.5 flex flex-col gap-1.5">
      {/*
        Row 1: street (flexes) + apt (narrow companion). Inlining
        apt next to street saves a row and matches the way address
        forms behave on most native iOS / web checkout flows.
      */}
      <div className="grid grid-cols-[1fr_104px] gap-1.5">
        <NestedField
          autoComplete="address-line1"
          autoFocus
          invalid={missedLine1}
          label="Street address"
          name="address-line1"
          onChange={(event) => onChange({ line1: event.target.value })}
          placeholder={poBox ? 'PO Box 1234' : '123 Main St'}
          type="text"
          value={kyc.line1}
        />
        <NestedField
          autoComplete="address-line2"
          label="Apt"
          name="address-line2"
          onChange={(event) => onChange({ line2: event.target.value })}
          placeholder="4B"
          type="text"
          value={kyc.line2}
        />
      </div>
      {single === 'line1' && <FieldErrorMessage>{singleMessage}</FieldErrorMessage>}
      <NestedField
        autoComplete="address-level2"
        invalid={missedCity}
        label="City"
        name="city"
        onChange={(event) => onChange({ city: event.target.value })}
        placeholder="City"
        type="text"
        value={kyc.city}
      />
      {single === 'city' && <FieldErrorMessage>{singleMessage}</FieldErrorMessage>}
      {/*
        State + ZIP on their own row. Explicit `104px_1fr` cols so
        ZIP never overflows when the user types `ZIP+4` (e.g.
        `10001-1234`) — and so the State dropdown's height matches
        the ZIP NestedField at any zoom.
      */}
      <div className="grid grid-cols-[104px_1fr] gap-1.5">
        <CompactStateSelect
          invalid={missedState}
          onChange={(value) => onChange({ state: value })}
          value={kyc.state}
        />
        <NestedField
          autoComplete="postal-code"
          inputMode="numeric"
          invalid={missedZip}
          label="ZIP"
          maxLength={10}
          name="postal-code"
          // Auto-strip everything except digits + the single dash
          // separator used in ZIP+4. Locks the field into a valid
          // shape as the user types so the Continue button can't
          // get stuck disabled with no visible cause (user feedback
          // May 15 — "all fields filled but no continue button").
          onChange={(event) => onChange({ postalCode: formatPostalCode(event.target.value) })}
          placeholder="10001"
          type="text"
          value={kyc.postalCode}
        />
        {(single === 'state' || single === 'zip') && (
          <div className="col-span-2">
            <FieldErrorMessage>{singleMessage}</FieldErrorMessage>
          </div>
        )}
      </div>
      {/*
        Compact side-by-side opt-outs — half-width each, shorter
        copy, so they read as form options rather than a vertical
        wall of "I don't have…" sentences.
      */}
      <div className="mt-0.5 grid grid-cols-2 gap-1.5">
        <Checkbox checked={poBox} label="PO box only" onChange={() => setPoBox((v) => !v)} />
        <Checkbox checked={false} label="No fixed address" onChange={() => onBlock('no-address')} />
      </div>
      {multiMessage && <FieldErrorMessage>{multiMessage}</FieldErrorMessage>}
    </div>
  )
}

/**
 * Strip everything except digits + a single dash separator (for ZIP+4)
 * and cap at `12345-6789`. Used on the postal-code input so the field
 * is impossible to fill with letters, spaces, or stray punctuation,
 * which would otherwise silently disable the Continue button.
 */
function formatPostalCode(value: string): string {
  const cleaned = value.replace(/[^\d-]/g, '')
  const [head = '', ...rest] = cleaned.split('-')
  const fiveDigits = head.replace(/\D/g, '').slice(0, 5)
  if (rest.length === 0) return fiveDigits
  const fourDigits = rest.join('').replace(/\D/g, '').slice(0, 4)
  if (fiveDigits.length < 5) return fiveDigits
  return `${fiveDigits}-${fourDigits}`
}

/**
 * Format an SSN with inline dashes (`123-45-6789`) as the user types.
 * Mirrors the way the date input auto-inserts slashes between the
 * MM / DD / YYYY segments — visible digits, fixed separators, no
 * password masking that would otherwise render as confusing dots.
 */
function formatSsnDash(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 9)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

/**
 * Shared square icon-button used inside a `NestedField` suffix slot
 * (calendar, eye, clear, etc.). Sized to align with the field's
 * typography and centered by the parent `NestedField`'s grid row.
 */
function IconSuffixButton(props: {
  'aria-label': string
  'aria-pressed'?: boolean
  icon: ReactNode
  onClick: () => void
}) {
  const { icon, onClick } = props
  return (
    <button
      aria-label={props['aria-label']}
      aria-pressed={props['aria-pressed']}
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-button border border-border bg-surface text-foreground-secondary transition-colors hover:bg-secondary-hover hover:text-foreground focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-2 focus-visible:outline-focus"
      onClick={onClick}
      type="button"
    >
      {icon}
    </button>
  )
}

/** Compact lightweight checkbox used for KYC opt-outs. */
function Checkbox(props: { checked: boolean; label: ReactNode; onChange: () => void }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 px-1 py-1 text-label-13 text-foreground-secondary hover:text-foreground">
      <input
        checked={props.checked}
        className="size-3.5 cursor-pointer accent-foreground"
        onChange={props.onChange}
        type="checkbox"
      />
      <span>{props.label}</span>
    </label>
  )
}

/**
 * Compact state selector for the polish address row. Built directly
 * on Base UI's `Select` so the trigger can adopt the same
 * floating-label layout as `NestedField` (mono uppercase `STATE`
 * caption above the value), keeping it visually aligned with the
 * adjacent ZIP input. Flips into the error palette when `invalid`
 * is true to mirror the floating-label fields' feedback.
 */
function CompactStateSelect(props: {
  invalid?: boolean
  onChange: (value: string) => void
  value: string
}) {
  const { invalid, onChange, value } = props
  return (
    <BaseSelect.Root
      onValueChange={(next) => {
        if (typeof next === 'string') onChange(next)
      }}
      value={value}
    >
      <BaseSelect.Trigger
        aria-label="State"
        className={
          'grid min-w-[88px] grid-cols-[1fr_auto] items-center gap-x-2 rounded-body border px-3 pt-2 pb-1.5 text-left transition-colors duration-150 outline-none ' +
          (invalid
            ? 'border-error/40 bg-error-subtle/30 hover:bg-error-subtle/40 focus-visible:border-error/70'
            : 'border-transparent bg-secondary hover:bg-secondary-hover focus-visible:border-focus focus-visible:bg-secondary-hover')
        }
        data-invalid={invalid || undefined}
      >
        <span className="flex min-w-0 flex-col gap-1">
          <span
            className={
              'font-mono text-[10px] leading-none uppercase tracking-[0.03em] ' +
              (invalid ? 'text-error' : 'text-foreground-secondary')
            }
          >
            State
          </span>
          <span className="min-w-0 text-copy-14 leading-tight text-foreground">
            <BaseSelect.Value>
              {(next) =>
                typeof next === 'string' && next.length > 0 ? (
                  next
                ) : (
                  <span className="text-foreground-secondary/50">Select</span>
                )
              }
            </BaseSelect.Value>
          </span>
        </span>
        <BaseSelect.Icon className="shrink-0 text-foreground-secondary">
          <ChevronDown className="size-4" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>

      <BaseSelect.Portal>
        <BaseSelect.Positioner
          alignItemWithTrigger={false}
          className="z-50 outline-none"
          sideOffset={4}
        >
          <BaseSelect.Popup className="max-h-72 min-w-[var(--anchor-width)] overflow-auto rounded-body border border-border bg-surface py-1 shadow-lg">
            {usStates.map((state) => (
              <BaseSelect.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-label-14 text-foreground outline-none data-highlighted:bg-secondary-hover data-selected:font-medium"
                key={state.code}
                value={state.code}
              >
                <BaseSelect.ItemText>{state.code}</BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}

/**
 * Mock document-upload area used when the user opts out of SSN in
 * the identity sub-step. Single tile for a generic government ID.
 */
function IdUploadDropZone() {
  return <DocumentDropTile label="Government-issued ID" />
}

/**
 * Native KYC document-upload state for `step === 'kyc-2'`. Renders a
 * document-type picker and one or two upload tiles. The tiles are
 * mock: clicking simulates a file pick by flipping to a "selected"
 * state with a filename + checkmark. The footer's "Verify identity"
 * CTA advances the flow.
 */
function NativeDocumentUpload() {
  const [docType, setDocType] = useState<'license' | 'state-id' | 'passport'>('license')
  const isPassport = docType === 'passport'
  return (
    <div className="flex flex-col gap-2">
      <DocumentTypeSelect onChange={setDocType} value={docType} />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <DocumentDropTile fileName="license-front.jpg" label="Front of ID" />
        {!isPassport && <DocumentDropTile fileName="license-back.jpg" label="Back of ID" />}
      </div>
      <p className="text-label-12 text-foreground-secondary">
        We accept driver’s license, state ID, and passport. PDF, PNG, or JPG up to 8MB.
      </p>
    </div>
  )
}

/**
 * Single drop-zone tile used by `NativeDocumentUpload` and the
 * SSN-opt-out path in `KycIdentitySubStep`. Click to simulate a file
 * pick; tile flips to a "selected" state with a checkmark and the
 * mock filename. Click again to clear.
 */
function DocumentDropTile(props: { label: string; fileName?: string }) {
  const { fileName = 'photo.jpg', label } = props
  const [picked, setPicked] = useState(false)
  return (
    <button
      aria-pressed={picked}
      className={
        'flex flex-col items-center gap-2 rounded-body border border-dashed px-4 py-6 text-center transition-colors duration-150 outline-none ' +
        (picked
          ? 'border-positive/40 bg-positive-subtle/20 hover:bg-positive-subtle/30'
          : 'border-border bg-pane hover:border-border-hover hover:bg-secondary/40 focus-visible:border-focus')
      }
      onClick={() => setPicked((v) => !v)}
      type="button"
    >
      <span
        className={
          'inline-flex size-9 items-center justify-center rounded-full ' +
          (picked ? 'bg-positive-subtle text-positive' : 'bg-secondary text-foreground-secondary')
        }
      >
        {picked ? (
          <CheckCircle aria-hidden className="size-4" />
        ) : (
          <Upload aria-hidden className="size-4" />
        )}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.03em] text-foreground-secondary">
        {label}
      </span>
      <span className="text-label-13 text-foreground">
        {picked ? fileName : 'Drag or click to upload'}
      </span>
    </button>
  )
}

/**
 * Floating-label document-type select. Same trigger styling as the
 * other `NestedField`s in the KYC flow so the picker feels native to
 * the form rather than a separate widget.
 */
function DocumentTypeSelect(props: {
  onChange: (value: 'license' | 'state-id' | 'passport') => void
  value: 'license' | 'state-id' | 'passport'
}) {
  const { onChange, value } = props
  const options: readonly { id: 'license' | 'state-id' | 'passport'; label: string }[] = [
    { id: 'license', label: 'Driver’s license' },
    { id: 'state-id', label: 'State ID' },
    { id: 'passport', label: 'Passport' },
  ]
  const current = options.find((o) => o.id === value) ?? options[0]!
  return (
    <BaseSelect.Root
      onValueChange={(next) => {
        if (next === 'license' || next === 'state-id' || next === 'passport') onChange(next)
      }}
      value={value}
    >
      <BaseSelect.Trigger
        aria-label="Document type"
        className="grid grid-cols-[1fr_auto] items-center gap-x-2 rounded-body border border-transparent bg-secondary px-3 pt-2 pb-1.5 text-left transition-colors duration-150 outline-none hover:bg-secondary-hover focus-visible:border-focus focus-visible:bg-secondary-hover"
      >
        <span className="flex min-w-0 flex-col gap-1">
          <span className="font-mono text-[10px] leading-none uppercase tracking-[0.03em] text-foreground-secondary">
            Document type
          </span>
          <span className="min-w-0 text-copy-14 leading-tight text-foreground">
            {current.label}
          </span>
        </span>
        <BaseSelect.Icon className="shrink-0 text-foreground-secondary">
          <ChevronDown className="size-4" />
        </BaseSelect.Icon>
      </BaseSelect.Trigger>

      <BaseSelect.Portal>
        <BaseSelect.Positioner
          alignItemWithTrigger={false}
          className="z-50 outline-none"
          sideOffset={4}
        >
          <BaseSelect.Popup className="min-w-[var(--anchor-width)] overflow-auto rounded-body border border-border bg-surface py-1 shadow-lg">
            {options.map((o) => (
              <BaseSelect.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-label-14 text-foreground outline-none data-highlighted:bg-secondary-hover data-selected:font-medium"
                key={o.id}
                value={o.id}
              >
                <BaseSelect.ItemText>{o.label}</BaseSelect.ItemText>
              </BaseSelect.Item>
            ))}
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  )
}

function CardBrandIcon(props: { brand: string | null; className?: string }) {
  const brand = (props.brand ?? '').toLowerCase()
  // Visa: official navy (#1A1F71) with an italic-bold VISA wordmark
  // rendered as text so it stays crisp at small sizes and matches
  // the visual weight of the other card chips (Amex, Discover).
  if (brand === 'visa')
    return (
      <svg
        aria-hidden
        className={props.className}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        viewBox="0 0 36 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="36" height="24" rx="3" fill="#1A1F71" />
        <text
          x="18"
          y="15.6"
          textAnchor="middle"
          fill="#FFFFFF"
          style={{
            font: '900 italic 9px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif',
            letterSpacing: '0.6px',
          }}
        >
          VISA
        </text>
      </svg>
    )
  // Mastercard: classic two-circle mark on white with a thin border.
  // No wordmark on the small chip — the interlocked discs are the
  // recognizable element.
  if (brand === 'mastercard' || brand === 'master_card')
    return (
      <svg
        aria-hidden
        className={props.className}
        role="img"
        viewBox="0 0 36 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.5" y="0.5" width="35" height="23" rx="3" fill="#FFFFFF" stroke="#E5E7EB" />
        <circle cx="14" cy="12" r="6" fill="#EB001B" />
        <circle cx="22" cy="12" r="6" fill="#F79E1B" />
        <path d="M18 7.6a6 6 0 0 1 0 8.8 6 6 0 0 1 0-8.8z" fill="#FF5F00" />
      </svg>
    )
  // Amex: cleaner blue background + a centered AMEX wordmark, slightly
  // larger so it reads at small sizes.
  if (brand === 'amex' || brand === 'american_express')
    return (
      <svg
        aria-hidden
        className={props.className}
        role="img"
        viewBox="0 0 36 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="36" height="24" rx="3" fill="#006FCF" />
        <text
          x="18"
          y="15.4"
          textAnchor="middle"
          fill="#FFFFFF"
          style={{ font: '800 8px ui-sans-serif, system-ui', letterSpacing: '0.5px' }}
        >
          AMEX
        </text>
      </svg>
    )
  if (brand === 'discover')
    return (
      <svg
        aria-hidden
        className={props.className}
        role="img"
        viewBox="0 0 36 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.5" y="0.5" width="35" height="23" rx="3" fill="#FFFFFF" stroke="#E5E7EB" />
        <circle cx="29" cy="12" r="4" fill="#FF6000" />
        <text
          x="14"
          y="15"
          textAnchor="middle"
          fill="#111827"
          style={{ font: '700 6px ui-sans-serif, system-ui', letterSpacing: '0.2px' }}
        >
          DISCOVER
        </text>
      </svg>
    )
  return (
    <svg
      aria-hidden
      className={props.className}
      role="img"
      viewBox="0 0 36 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="0.5"
        y="0.5"
        width="35"
        height="23"
        rx="3"
        fill="rgba(127,127,127,0.08)"
        stroke="rgba(127,127,127,0.25)"
      />
      <rect x="4" y="7" width="9" height="2.5" rx="1" fill="rgba(127,127,127,0.45)" />
      <rect x="4" y="13" width="16" height="2" rx="1" fill="rgba(127,127,127,0.35)" />
    </svg>
  )
}

function toTitleCase(value: string) {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => `${word[0]?.toUpperCase() ?? ''}${word.slice(1).toLowerCase()}`)
    .join(' ')
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

function normalizePhone(value: string) {
  const trimmed = value.trim()
  const digits = trimmed.replace(/\D/g, '')
  if (trimmed.startsWith('+')) {
    if (digits.length < 11 || digits.length > 15) return undefined
    return `+${digits}`
  }
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return undefined
}

const usStates = [
  { code: 'AL', label: 'Alabama' },
  { code: 'AK', label: 'Alaska' },
  { code: 'AZ', label: 'Arizona' },
  { code: 'AR', label: 'Arkansas' },
  { code: 'CA', label: 'California' },
  { code: 'CO', label: 'Colorado' },
  { code: 'CT', label: 'Connecticut' },
  { code: 'DE', label: 'Delaware' },
  { code: 'DC', label: 'District of Columbia' },
  { code: 'FL', label: 'Florida' },
  { code: 'GA', label: 'Georgia' },
  { code: 'HI', label: 'Hawaii' },
  { code: 'ID', label: 'Idaho' },
  { code: 'IL', label: 'Illinois' },
  { code: 'IN', label: 'Indiana' },
  { code: 'IA', label: 'Iowa' },
  { code: 'KS', label: 'Kansas' },
  { code: 'KY', label: 'Kentucky' },
  { code: 'LA', label: 'Louisiana' },
  { code: 'ME', label: 'Maine' },
  { code: 'MD', label: 'Maryland' },
  { code: 'MA', label: 'Massachusetts' },
  { code: 'MI', label: 'Michigan' },
  { code: 'MN', label: 'Minnesota' },
  { code: 'MS', label: 'Mississippi' },
  { code: 'MO', label: 'Missouri' },
  { code: 'MT', label: 'Montana' },
  { code: 'NE', label: 'Nebraska' },
  { code: 'NV', label: 'Nevada' },
  { code: 'NH', label: 'New Hampshire' },
  { code: 'NJ', label: 'New Jersey' },
  { code: 'NM', label: 'New Mexico' },
  { code: 'NY', label: 'New York' },
  { code: 'NC', label: 'North Carolina' },
  { code: 'ND', label: 'North Dakota' },
  { code: 'OH', label: 'Ohio' },
  { code: 'OK', label: 'Oklahoma' },
  { code: 'OR', label: 'Oregon' },
  { code: 'PA', label: 'Pennsylvania' },
  { code: 'RI', label: 'Rhode Island' },
  { code: 'SC', label: 'South Carolina' },
  { code: 'SD', label: 'South Dakota' },
  { code: 'TN', label: 'Tennessee' },
  { code: 'TX', label: 'Texas' },
  { code: 'UT', label: 'Utah' },
  { code: 'VT', label: 'Vermont' },
  { code: 'VA', label: 'Virginia' },
  { code: 'WA', label: 'Washington' },
  { code: 'WV', label: 'West Virginia' },
  { code: 'WI', label: 'Wisconsin' },
  { code: 'WY', label: 'Wyoming' },
] as const
